import { defineStore } from 'pinia';
import { ref, computed, watchEffect } from 'vue';
import {
  findActiveGoalSet,
  type GoalSet,
} from '@/services/goalsService';
import {
  getFoodItems,
  type SyncableFoodItem,
} from '@/services/foodItems';
import {
  getDiaryDay,
  patchDiaryDay,
  getDiaryDaysInRange,
  getAllDiaryDays,
  subscribeToDay,
  emptyDiaryDayPlanGeneration,
  emptyDiaryDayBody,
  resolveMealTarget,
  resolveMealExercise,
  resolveMealPlannedAt,
  resolveMealTitle,
  isMealCustomized,
  type DiaryDay,
  type DiaryDayBody,
  type DiaryDayEntry,
  type DiaryDayGoalSnapshot,
  type DiaryMeal,
  type DiaryMealCustom,
  type DiaryMealExercise,
  type DiaryDayImportedTotals,
  type DiaryDayPlanGeneration,
} from '@/services/diaryDays';
import { goalsService, type Goal } from '@/services/goalsService';
import {
  computeFoodTotals,
  computeEffectiveTotals,
  computeExerciseCaloriesBurned,
  computeMealTotals,
  computeMealPlanTotals,
  resolveDailyGoals,
  sortedEntries,
  sortedMealPlans,
  foodEntries as foodEntriesOf,
  exerciseEntries as exerciseEntriesOf,
  emptyTotals,
  type DailyGoalTargets,
  type MacroTotals,
} from '@/utils/diaryDayTotals';
import {
  uncompletePlan as uncompletePlanInBody,
  recalculateMealPlans,
  defaultPeriodization,
  resolvePlanKind,
} from '@/services/mealPeriodization';
import { backfillDiaryDay } from '@/services/diaryDayBackfill';
import { formatDateInput, getLoggedDate, toUtcIsoFromLocalDateTime } from '@/utils/dateTime';
import { useAuthSessionStore } from '@/stores/authSessionStore';
import { useDiarySettingsStore } from '@/stores/diarySettingsStore';
import { useGoalsStore } from '@/stores/goalsStore';
import { useWeightStore } from '@/stores/weightStore';
import { useMealsStore } from '@/stores/mealsStore';
import { configuredMealPlanId, inferMealType, type MealId } from '@macro-tracker/models';
import type { ExerciseIntensity } from '@/services/exerciseTypes';
import type { ExercisePlan, MacroTarget } from '@/services/diaryPlanning';
import type { FoodItem, Nutrition, NutritionLabelNutrients, FoodItemServingSize as ServingSize } from '@macro-tracker/models';
import { nutritionPlannerClient } from '@/services/nutritionPlannerApi';
import { generateId } from '@/utils/id';

// ---------------------------------------------------------------------------
// Legacy-compatible shapes — projected from DiaryDay body so existing views
// and components keep working unchanged.
// ---------------------------------------------------------------------------

// The persisted entry (DiaryDayEntry) lives inside a meal and has no meal
// linkage of its own. Callers still pass a target `mealId` (configured meal) or
// `mealPlanId` (meal id) to say where a food should be logged; these are
// transient routing hints stripped before the entry is stored in its meal.
export type DiaryEntry = DiaryDayEntry & {
  mealId?: number | null;
  mealPlanId?: string | null;
};

const stripEntryRouting = (entry: DiaryEntry): DiaryDayEntry => {
  const { mealId: _mealId, mealPlanId: _mealPlanId, ...rest } = entry;
  return rest;
};

const UNSORTED_MEAL_ID = 'unsorted';

export type DiaryMealPlan = {
  id: string;
  externalSource?: DiaryMeal['externalSource'];
  date: string;
  plannedAt: string;
  mealId: number | null;
  kind?: DiaryMeal['kind'];
  title: string;
  targetCalories: number;
  targetProtein: number;
  targetCarbs: number;
  targetFat: number;
  exerciseDurationMinutes?: number | null;
  exerciseIntensity?: ExerciseIntensity | null;
  exerciseCaloriesBurned?: number | null;
  exerciseCarbsPerHour?: number | null;
  exerciseCarbsConsumed?: number | null;
  exerciseCaloriesConsumed?: number | null;
  exerciseFuelSource?: 'provided' | 'calculated' | null;
  workoutActuals?: DiaryMeal['workoutActuals'];
  workoutSupportSnack?: DiaryMeal['workoutSupportSnack'];
  workoutSupportSnackSuppression?: DiaryMeal['workoutSupportSnackSuppression'];
  periodization?: DiaryMeal['periodization'];
  // Partial user overrides (replaces the old periodization.userOverrides/locks).
  custom?: DiaryMealCustom;
  completedAt?: string | null;
  skippedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
};

export type DiaryDayPlanCompat = {
  id: string;
  date: string;
  targetCalories: number;
  targetProtein: number;
  targetCarbs: number;
  targetFat: number;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
};

export type ImportedTotalCompat = {
  id: string;
  date: string;
  importSource: 'chronometer' | 'manual';
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  labelNutrients?: DiaryDayImportedTotals['labelNutrients'];
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
};

export type { ExercisePlan, MacroTarget, MealId, DiaryDayBody, DiaryDay };

export type DiaryPlanningAddFoodTarget = {
  date: string;
  mealId: MealId | null;
  mealPlanId: string | null;
};

const IMPORTED_TOTAL_ID = (date: string): string => `imported:${date}`;
const INTERVALS_ICU_EVENT_ID_PREFIX = 'intervals-icu:event:';
const INTERVALS_ICU_ACTIVITY_ID_PREFIX = 'intervals-icu:activity:';

const intervalsPlanExternalId = (plan: DiaryMeal): string | null => {
  if (plan.externalSource?.provider === 'intervals_icu') {
    const externalId = plan.externalSource.id.trim();
    if (externalId) return externalId;
  }
  if (!plan.id.startsWith(INTERVALS_ICU_EVENT_ID_PREFIX)) {
    return null;
  }
  const externalId = plan.id.slice(INTERVALS_ICU_EVENT_ID_PREFIX.length).trim();
  return externalId || null;
};

const intervalsActivityExternalId = (entry: DiaryDayEntry): string | null => {
  if (entry.externalSource?.provider === 'intervals_icu') {
    const externalId = entry.externalSource.id.trim();
    if (externalId) return externalId;
  }
  if (!entry.id.startsWith(INTERVALS_ICU_ACTIVITY_ID_PREFIX)) {
    return null;
  }
  const externalId = entry.id.slice(INTERVALS_ICU_ACTIVITY_ID_PREFIX.length).trim();
  return externalId || null;
};

const normalizedWorkoutText = (value: string | undefined): string =>
  value?.trim().toLocaleLowerCase() ?? '';

const workoutEntryFingerprint = (entry: DiaryDayEntry): string =>
  JSON.stringify({
    title: normalizedWorkoutText(entry.source.label),
    loggedAt: entry.loggedAt,
    durationMinutes: entry.exercise?.durationMinutes ?? null,
    intensity: entry.exercise?.intensity ?? null,
    caloriesBurned: entry.nutritionSnapshot.calories ?? null,
  });

const suppressIntervalsActivity = (
  body: DiaryDayBody,
  externalId: string,
  suppressedAt: string,
  parentId?: string,
): void => {
  const existing = (body.externalSourceSuppressions ?? []).find((suppression) =>
    suppression.provider === 'intervals_icu'
    && suppression.entityType === 'activity'
    && suppression.id === externalId);
  body.externalSourceSuppressions = [
    ...(body.externalSourceSuppressions ?? []).filter((suppression) =>
      suppression.provider !== 'intervals_icu'
      || suppression.entityType !== 'activity'
      || suppression.id !== externalId),
    {
      provider: 'intervals_icu',
      entityType: 'activity',
      id: externalId,
      ...(existing?.parentId || parentId
        ? { parentId: existing?.parentId ?? parentId }
        : {}),
      suppressedAt,
    },
  ];
};

const restoreIntervalsWorkoutSuppressions = (
  body: DiaryDayBody,
  eventExternalId: string,
): void => {
  body.externalSourceSuppressions = (body.externalSourceSuppressions ?? [])
    .filter((suppression) =>
      suppression.provider !== 'intervals_icu'
      || suppression.entityType !== 'activity'
      || suppression.parentId !== eventExternalId);
};

const externalSourceForDeletedPlan = (
  plan: DiaryMeal,
  suppressedAt: string,
): DiaryMeal['externalSource'] => {
  if (plan.externalSource) {
    return { ...plan.externalSource, suppressedAt };
  }
  if (!plan.id.startsWith(INTERVALS_ICU_EVENT_ID_PREFIX)) {
    return undefined;
  }
  const id = plan.id.slice(INTERVALS_ICU_EVENT_ID_PREFIX.length).trim();
  return id
    ? { provider: 'intervals_icu', id, suppressedAt }
    : undefined;
};

const emptyMealTarget = () => ({ calories: 0, protein: 0, carbs: 0, fat: 0 });

const toMealPlanCompat = (
  plan: DiaryMeal,
  date: string,
): DiaryMealPlan => {
  const target = resolveMealTarget(plan) ?? emptyMealTarget();
  const exercise = resolveMealExercise(plan);
  const plannerFuel = plan.plannerResult?.workoutFuelingTarget ?? null;
  const useExplicitExerciseFuel = Boolean(
    hasExerciseFuelValues(exercise) && !hasPlannerGeneratedFuelInExercise(plan),
  );
  const explicitFuelSource =
    plan.workoutActuals?.fuelCarbsPerHour !== null && plan.workoutActuals?.fuelCarbsPerHour !== undefined
      ? 'provided'
      : useExplicitExerciseFuel && exercise?.carbsPerHour !== null && exercise?.carbsPerHour !== undefined
        ? 'provided'
        : null;
  const plannerFuelSource = plannerFuel ? (plannerFuel.source ?? 'calculated') : null;
  return {
    id: plan.id,
    externalSource: plan.externalSource,
    date,
    plannedAt: resolveMealPlannedAt(plan),
    mealId: plan.mealId,
    kind: plan.kind ?? resolvePlanKind(plan),
    title: resolveMealTitle(plan),
    targetCalories: target.calories,
    targetProtein: target.protein,
    targetCarbs: target.carbs,
    targetFat: target.fat,
    exerciseDurationMinutes: exercise?.durationMinutes ?? null,
    exerciseIntensity: exercise?.intensity ?? null,
    exerciseCaloriesBurned: exercise?.caloriesBurned ?? null,
    exerciseCarbsPerHour: useExplicitExerciseFuel
      ? exercise?.carbsPerHour ?? null
      : plannerFuel?.carbsPerHour ?? null,
    exerciseCarbsConsumed: useExplicitExerciseFuel
      ? exercise?.carbsConsumed ?? null
      : plannerFuel?.carbsG ?? null,
    exerciseCaloriesConsumed: useExplicitExerciseFuel
      ? exercise?.caloriesConsumed ?? null
      : plannerFuel?.calories ?? null,
    exerciseFuelSource: explicitFuelSource ?? plannerFuelSource,
    workoutActuals: plan.workoutActuals ?? null,
    workoutSupportSnack: plan.workoutSupportSnack ?? null,
    workoutSupportSnackSuppression: plan.workoutSupportSnackSuppression,
    periodization: plan.periodization,
    custom: plan.custom,
    completedAt: plan.completedAt ?? null,
    skippedAt: plan.skippedAt ?? null,
    deletedAt: plan.deletedAt ?? null,
  };
};

const fromMealPlanCompat = (
  plan: DiaryMealPlan,
  existing?: DiaryMeal,
): DiaryMeal => ({
  id: plan.id,
  externalSource: plan.externalSource ?? existing?.externalSource,
  kind: plan.kind ?? existing?.kind,
  mealId: plan.mealId,
  planned: {
    title: plan.title,
    plannedAt: plan.plannedAt,
    target: {
      calories: plan.targetCalories,
      protein: plan.targetProtein,
      carbs: plan.targetCarbs,
      fat: plan.targetFat,
    },
    exercise:
      plan.exerciseIntensity ||
      plan.exerciseCaloriesBurned != null ||
      plan.exerciseDurationMinutes != null
        ? {
            durationMinutes: plan.exerciseDurationMinutes ?? null,
            intensity: plan.exerciseIntensity ?? null,
            caloriesBurned: plan.exerciseCaloriesBurned ?? null,
            carbsPerHour: plan.exerciseCarbsPerHour ?? null,
            carbsConsumed: plan.exerciseCarbsConsumed ?? null,
            caloriesConsumed: plan.exerciseCaloriesConsumed ?? null,
          }
        : null,
  },
  custom: plan.custom ?? existing?.custom,
  entries: existing?.entries ?? [],
  workoutActuals: plan.workoutActuals ?? existing?.workoutActuals ?? null,
  workoutSupportSnack: plan.workoutSupportSnack ?? existing?.workoutSupportSnack ?? null,
  workoutSupportSnackSuppression:
    plan.workoutSupportSnackSuppression ?? existing?.workoutSupportSnackSuppression,
  periodization: plan.periodization ?? existing?.periodization,
  plannerResult: existing?.plannerResult,
  completedAt: plan.completedAt ?? existing?.completedAt ?? null,
  skippedAt: plan.skippedAt ?? existing?.skippedAt ?? null,
  deletedAt:
    plan.deletedAt !== undefined
      ? plan.deletedAt
      : existing?.deletedAt ?? null,
});

const hasMealTarget = (target: { calories: number; protein: number; carbs: number; fat: number } | null): boolean =>
  target !== null &&
  (target.calories > 0 ||
  target.protein > 0 ||
  target.carbs > 0 ||
  target.fat > 0);

const findActiveMealForMealId = (
  body: DiaryDayBody,
  mealId: MealId,
): DiaryMeal | null =>
  body.meals.find(
    (plan) =>
      !plan.deletedAt &&
      plan.mealId === mealId &&
      resolvePlanKind(plan) === 'meal',
  ) ?? null;

/**
 * Resolve which meal a newly-logged entry should live in. Prefers an active
 * meal-kind meal matching the entry's target config mealId; returns null when
 * none exists (caller falls back to an Unsorted meal).
 */
const resolveTargetMeal = (
  body: DiaryDayBody,
  mealId: number | null | undefined,
): DiaryMeal | null => {
  if (mealId === null || mealId === undefined) return null;
  return findActiveMealForMealId(body, mealId as MealId);
};

/** Find (or create + append) the synthetic Unsorted meal that holds foods with no meal. */
const ensureUnsortedMeal = (body: DiaryDayBody): DiaryMeal => {
  let meal = body.meals.find((m) => m.id === UNSORTED_MEAL_ID && !m.deletedAt);
  if (meal) return meal;
  meal = {
    id: UNSORTED_MEAL_ID,
    mealId: null,
    kind: 'meal',
    planned: {
      title: 'Unsorted',
      plannedAt: new Date().toISOString(),
      target: null,
      exercise: null,
    },
    entries: [],
  };
  body.meals.push(meal);
  return meal;
};

/** Resolve the meal an entry should be stored in, given its routing hints. */
const resolveMealForEntry = (body: DiaryDayBody, entry: DiaryEntry): DiaryMeal => {
  if (entry.mealPlanId) {
    const byId = body.meals.find((m) => m.id === entry.mealPlanId && !m.deletedAt);
    if (byId) return byId;
  }
  const byConfigMeal = resolveTargetMeal(body, entry.mealId);
  if (byConfigMeal) return byConfigMeal;
  return ensureUnsortedMeal(body);
};

/** Remove an entry (by id) from whichever meal currently holds it. */
const removeEntryFromMeals = (body: DiaryDayBody, entryId: string): DiaryDayEntry | null => {
  for (const meal of body.meals) {
    const idx = meal.entries.findIndex((e) => e.id === entryId);
    if (idx !== -1) {
      const [removed] = meal.entries.splice(idx, 1);
      return removed;
    }
  }
  return null;
};

/** Insert or move an entry into the meal indicated by its routing hints. */
const upsertEntryIntoMeals = (body: DiaryDayBody, entry: DiaryEntry): void => {
  removeEntryFromMeals(body, entry.id);
  const meal = resolveMealForEntry(body, entry);
  meal.entries.push(stripEntryRouting(entry));
};

const sumFoodEntriesForPlan = (
  plan: DiaryMeal,
): MacroTotals =>
  plan.entries.reduce<MacroTotals>((sum, entry) => {
    if (entry.source.type === 'exercise') return sum;
    return {
      calories: sum.calories + (entry.nutritionSnapshot.calories ?? 0),
      protein: sum.protein + (entry.nutritionSnapshot.protein ?? 0),
      carbs: sum.carbs + (entry.nutritionSnapshot.carbs ?? 0),
      fat: sum.fat + (entry.nutritionSnapshot.fat ?? 0),
    };
  }, emptyTotals());

const sumFoodEntriesForMealSlot = (
  body: DiaryDayBody,
  slot: { mealId: MealId | null },
  plan: DiaryMeal | null,
): MacroTotals => {
  if (plan) return sumFoodEntriesForPlan(plan);
  if (slot.mealId === null) return emptyTotals();
  const meal = findActiveMealForMealId(body, slot.mealId);
  return meal ? sumFoodEntriesForPlan(meal) : emptyTotals();
};

type NutritionPlannerRequest = Parameters<typeof nutritionPlannerClient.generatePlan>[0];
type NutritionPlannerResponse = Awaited<
  ReturnType<typeof nutritionPlannerClient.generatePlan>
>;
type PlannerMealTarget = Extract<
  NutritionPlannerResponse,
  { mealTargets: unknown[] }
>['mealTargets'][number];
type PlannerWorkoutFuelingTarget = Extract<
  NutritionPlannerResponse,
  { duringWorkoutTargets: unknown[] }
>['duringWorkoutTargets'][number];

type WorkoutSupportSnackTiming = NonNullable<DiaryMeal['workoutSupportSnack']>['timing'];

type PlannerMealSlot = {
  key: string;
  date: string;
  mealId: MealId | null;
  title: string;
  mealType: NonNullable<NutritionPlannerRequest['meals'][number]['mealType']>;
  startTime: string;
  requestTime: string;
  endTime: string;
  existingPlanId: string | null;
  workoutSupportSnack?: DiaryMeal['workoutSupportSnack'];
  canAutoRemoveWorkoutSupportSnack?: boolean;
};

type PlannerWindowContext = {
  date: string;
  body: DiaryDayBody;
  isSelectedDay: boolean;
  isHistorical: boolean;
};

type PlannerWorkoutSlot = {
  workoutId: string;
  fuelingId: string;
  date: string;
  planId: string;
  isSelectedDay: boolean;
  startTime: string;
  endTime: string;
  intensity: NutritionPlannerRequest['workouts'][number]['intensity'];
  caloriesBurned: number;
  completedAt?: string;
  carbsPerHourDuringWorkout?: number;
  actualFuelCalories?: number;
  actualFuelCarbsG?: number;
  fuelingTargetOverride?: NutritionPlannerRequest['duringWorkoutFueling'][number]['targetOverride'];
  suppressPreWorkoutSnack: boolean;
  suppressPostWorkoutSnack: boolean;
};

type PlannerWorkoutTargetRef = {
  date: string;
  planId: string;
  target: PlannerWorkoutFuelingTarget;
};

const DEFAULT_MEAL_WINDOW_MINUTES = 45;
const DEFAULT_WORKOUT_WINDOW_MINUTES = 60;
const PRE_WORKOUT_MEAL_BEFORE_MINUTES = 60;
const POST_WORKOUT_SNACK_WINDOW_MINUTES = 30;
const ADD_WORKOUT_SUPPORT_MEALS = false;
const PLANNER_MAX_WINDOW_DAYS = 5;
const MS_PER_MINUTE = 60_000;
const UNDER_FUELING_REASON_CODE = 'UNDER_FUELING_TARGETED';
type PlannerMealTargetOverride = NutritionPlannerRequest['mealOverrides'][number]['target'];

const clearPlanUserOverrides = (plan: DiaryMeal): DiaryMeal => {
  const periodization = plan.periodization ?? defaultPeriodization();
  const kind = resolvePlanKind(plan);
  const shouldClearExercise = kind !== 'meal';
  return {
    ...plan,
    kind: plan.kind ?? kind,
    // Reset all sticky overrides back to planner-provided values.
    custom: undefined,
    planned: shouldClearExercise
      ? { ...plan.planned, exercise: null }
      : plan.planned,
    periodization,
  };
};

const parsePlannerWorkoutTargetRef = (
  workoutId: string,
  selectedDate: string,
  target: PlannerWorkoutFuelingTarget,
): PlannerWorkoutTargetRef | null => {
  const marker = 'workout-';
  const scopedMarker = `:${marker}`;
  const scopedMarkerIndex = workoutId.indexOf(scopedMarker);
  const markerIndex = scopedMarkerIndex === -1
    ? (workoutId.startsWith(marker) ? 0 : -1)
    : scopedMarkerIndex + 1;
  if (markerIndex === -1) return null;
  const rawDate = markerIndex === 0 ? '' : workoutId.slice(0, markerIndex - 1);
  const planId = workoutId.slice(markerIndex + marker.length);
  if (!planId) return null;
  return {
    date: rawDate || selectedDate,
    planId,
    target,
  };
};

const isUnderFuelingTarget = (target: PlannerWorkoutFuelingTarget): boolean =>
  target.reasonCodes.includes(UNDER_FUELING_REASON_CODE);

const PLANNER_STATIC_CONFIG: NutritionPlannerRequest['config'] = {
  athleteProfile: {
    hasGutTrainingForHighCarb: false,
  },
  rollingWindow: {
    days: 5,
    goalCalorieDeltaByGoal: {
      lose: 0,
      maintain: 0,
      gain: 0,
    },
  },
  macroTargets: {
    proteinGPerKgBodyWeight: 1.8,
    fatGPerKgBodyWeight: 0.8,
  },
  mealPlanning: {
    defaultMealLimits: {
      calories: { min: 150, max: 1500 },
      proteinG: { min: 5, max: 100 },
      fatG: { min: 3, max: 80 },
      carbsG: { min: 5, max: 260 },
      hardLimitMode: 'hard',
    },
  },
  duringWorkoutFueling: {
    defaultCarbsPerHourByFuelClass: {
      train_low_candidate: 20,
      normally_fueled: 60,
      high_fuel_required: 90,
    },
    minCarbsPerHourByFuelClass: {
      train_low_candidate: 15,
      normally_fueled: 30,
      high_fuel_required: 60,
    },
    maxCarbsPerHourByFuelClass: {
      train_low_candidate: 45,
      normally_fueled: 90,
      high_fuel_required: 120,
    },
    absoluteMaxCarbsPerHour: 130,
    softWarningThresholdCarbsPerHour: 90,
    allowAbove90gPerHour: true,
    requireGutTrainingForAbove90gPerHour: false,
  },
  workoutProximityRules: {
    preWorkoutWindowHours: {
      min: 1,
      max: 4,
    },
    postWorkoutWindowHours: 4,
    closePreWorkoutWindowHours: 1,
    preWorkoutConstraints: {
      carbPriorityMultiplier: 2,
      proteinPriorityMultiplier: 1,
      fatPenaltyMultiplier: 1.2,
      carbsG: {
        min: 20,
      },
    },
    postWorkoutConstraints: {
      carbPriorityMultiplier: 1.2,
      proteinPriorityMultiplier: 2,
      fatPenaltyMultiplier: 1.1,
      proteinG: {
        min: 20,
      },
    },
  },
};

const mapExerciseIntensityToPlanner = (
  intensity: ExerciseIntensity | null | undefined,
): NutritionPlannerRequest['workouts'][number]['intensity'] => {
  if (intensity === 'low') return 'low';
  if (intensity === 'high') return 'high';
  return 'moderate';
};

const addMinutesIso = (iso: string, minutes: number): string => {
  const parsed = new Date(iso);
  if (!Number.isFinite(parsed.getTime())) return iso;
  return new Date(parsed.getTime() + minutes * MS_PER_MINUTE).toISOString();
};

const plannerScopedKey = (date: string, isSelectedDay: boolean, key: string): string =>
  isSelectedDay ? key : `${date}:${key}`;

const toNonNegativeNumber = (value: number | null | undefined): number =>
  Math.max(0, Number.isFinite(value ?? NaN) ? Number(value) : 0);

const toOptionalNonNegativeNumber = (value: number | null | undefined): number | null =>
  value === null || value === undefined ? null : toNonNegativeNumber(value);

const toOptionalPlannerNumber = (value: number | null | undefined): number | undefined =>
  value === null || value === undefined ? undefined : toNonNegativeNumber(value);

const numbersClose = (a: number | null | undefined, b: number | null | undefined): boolean =>
  a !== null &&
  a !== undefined &&
  b !== null &&
  b !== undefined &&
  Math.abs(a - b) < 0.05;

const hasPlannerGeneratedFuelInExercise = (
  plan: DiaryMeal,
  exercise: DiaryMealExercise | null = resolveMealExercise(plan),
): boolean => {
  const plannerFuel = plan.plannerResult?.workoutFuelingTarget;
  if (!exercise || !plannerFuel) return false;
  return (
    numbersClose(exercise.carbsPerHour, plannerFuel.carbsPerHour) &&
    numbersClose(exercise.carbsConsumed, plannerFuel.carbsG) &&
    numbersClose(exercise.caloriesConsumed, plannerFuel.calories)
  );
};

const hasExerciseFuelValues = (exercise: DiaryMealExercise | null): boolean =>
  (exercise?.carbsPerHour !== null && exercise?.carbsPerHour !== undefined) ||
  (exercise?.carbsConsumed !== null && exercise?.carbsConsumed !== undefined) ||
  (exercise?.caloriesConsumed !== null && exercise?.caloriesConsumed !== undefined);

const resolveWorkoutDurationMinutes = (plan: DiaryMeal): number =>
  // Per-field fallback (resolveMealExercise): a user-edited duration wins, and
  // an unedited one falls back to the planned value — so editing some other
  // field (e.g. logging fuel) never drops the planned duration.
  toOptionalNonNegativeNumber(plan.workoutActuals?.durationMinutes) ??
  toOptionalNonNegativeNumber(resolveMealExercise(plan)?.durationMinutes) ??
  DEFAULT_WORKOUT_WINDOW_MINUTES;

// A past workout feeds the planner's rolling balance only once an actual burn
// has been recorded for it (completed or not). A planned-only past workout may
// never have happened, so it is left out.
const hasRecordedActualBurn = (plan: DiaryMeal): boolean =>
  toOptionalNonNegativeNumber(plan.workoutActuals?.caloriesBurned) !== null;

const workoutSupportSnackTimingFromPlan = (
  plan: DiaryMeal,
): WorkoutSupportSnackTiming | null => {
  if (plan.workoutSupportSnack?.timing === 'pre' || plan.workoutSupportSnack?.timing === 'post') {
    return plan.workoutSupportSnack.timing;
  }

  const title = resolveMealTitle(plan).trim().toLowerCase();
  if (title.includes('pre-workout snack') || title.includes('pre workout snack')) {
    return 'pre';
  }
  if (title.includes('post-workout snack') || title.includes('post workout snack')) {
    return 'post';
  }
  return null;
};

const isInPreWorkoutSupportWindow = (
  mealStart: number,
  workoutStart: number,
  hours: number,
): boolean => {
  if (!Number.isFinite(mealStart) || !Number.isFinite(workoutStart) || workoutStart < mealStart) {
    return false;
  }
  return (workoutStart - mealStart) / (60 * MS_PER_MINUTE) <= hours;
};

const isInPostWorkoutSupportWindow = (
  mealStart: number,
  workoutEnd: number,
  minutes: number,
): boolean => {
  if (!Number.isFinite(mealStart) || !Number.isFinite(workoutEnd) || mealStart < workoutEnd) {
    return false;
  }
  return (mealStart - workoutEnd) / MS_PER_MINUTE <= minutes;
};

const deletedSupportSnackMatchesWorkout = (
  snack: DiaryMeal,
  workout: DiaryMeal,
  timing: WorkoutSupportSnackTiming,
  workoutStart: string,
  workoutEnd: string,
): boolean => {
  if (!snack.deletedAt) return false;

  if (snack.workoutSupportSnack) {
    return false;
  }

  if (
    snack.mealId !== null ||
    resolvePlanKind(snack) !== 'meal' ||
    workoutSupportSnackTimingFromPlan(snack) !== timing
  ) {
    return false;
  }

  const snackStart = Date.parse(resolveMealPlannedAt(snack));
  return timing === 'pre'
    ? isInPreWorkoutSupportWindow(
        snackStart,
        Date.parse(workoutStart),
        PLANNER_STATIC_CONFIG.workoutProximityRules.closePreWorkoutWindowHours,
      )
    : isInPostWorkoutSupportWindow(
        snackStart,
        Date.parse(workoutEnd),
        POST_WORKOUT_SNACK_WINDOW_MINUTES,
      );
};

const resolveWorkoutSupportSnackSuppression = (
  body: DiaryDayBody,
  workout: DiaryMeal,
  workoutStart: string,
  workoutEnd: string,
): NonNullable<DiaryMeal['workoutSupportSnackSuppression']> => ({
  preWorkout:
    workout.workoutSupportSnackSuppression?.preWorkout === true ||
    body.meals.some((plan) =>
      deletedSupportSnackMatchesWorkout(plan, workout, 'pre', workoutStart, workoutEnd),
    ),
  postWorkout:
    workout.workoutSupportSnackSuppression?.postWorkout === true ||
    body.meals.some((plan) =>
      deletedSupportSnackMatchesWorkout(plan, workout, 'post', workoutStart, workoutEnd),
    ),
});

const resolveSupportSnackOwner = (
  body: DiaryDayBody,
  snack: DiaryMeal,
): { workoutPlanId: string; timing: WorkoutSupportSnackTiming } | null => {
  const timing = workoutSupportSnackTimingFromPlan(snack);
  if (!timing) return null;
  if (snack.workoutSupportSnack) {
    return {
      workoutPlanId: snack.workoutSupportSnack.workoutPlanId,
      timing: snack.workoutSupportSnack.timing,
    };
  }

  const snackStart = Date.parse(resolveMealPlannedAt(snack));
  const candidates = body.meals
    .filter((plan) => !plan.deletedAt && resolvePlanKind(plan) !== 'meal')
    .map((plan) => {
      const workoutStart = Date.parse(resolveMealPlannedAt(plan));
      const workoutEnd = Date.parse(addMinutesIso(resolveMealPlannedAt(plan), resolveWorkoutDurationMinutes(plan)));
      const diff = timing === 'pre' ? workoutStart - snackStart : snackStart - workoutEnd;
      const inWindow = timing === 'pre'
        ? isInPreWorkoutSupportWindow(
            snackStart,
            workoutStart,
            PLANNER_STATIC_CONFIG.workoutProximityRules.closePreWorkoutWindowHours,
          )
        : isInPostWorkoutSupportWindow(
            snackStart,
            workoutEnd,
            POST_WORKOUT_SNACK_WINDOW_MINUTES,
          );
      return { plan, diff, inWindow };
    })
    .filter((candidate) => candidate.inWindow && Number.isFinite(candidate.diff))
    .sort((a, b) => a.diff - b.diff);

  const owner = candidates[0]?.plan;
  return owner ? { workoutPlanId: owner.id, timing } : null;
};

const withWorkoutSupportSnackSuppressed = (
  plan: DiaryMeal,
  timing: WorkoutSupportSnackTiming,
): DiaryMeal => ({
  ...plan,
  workoutSupportSnackSuppression: {
    ...plan.workoutSupportSnackSuppression,
    ...(timing === 'pre' ? { preWorkout: true } : { postWorkout: true }),
  },
});

const hasEntriesForPlan = (body: DiaryDayBody, planId: string): boolean =>
  (body.meals.find((meal) => meal.id === planId)?.entries.length ?? 0) > 0;

const canAutoRemoveWorkoutSupportSnack = (
  body: DiaryDayBody,
  plan: DiaryMeal,
): boolean =>
  !plan.deletedAt &&
  plan.mealId === null &&
  resolvePlanKind(plan) === 'meal' &&
  workoutSupportSnackTimingFromPlan(plan) !== null &&
  plan.plannerResult?.mealTarget != null &&
  !plan.completedAt &&
  !plan.skippedAt &&
  !isMealCustomized(plan) &&
  !hasEntriesForPlan(body, plan.id);

const collectAutoRemovableWorkoutSupportSnackIds = (
  body: DiaryDayBody,
  workoutPlanId: string,
): Set<string> => {
  const ids = new Set<string>();
  for (const plan of body.meals) {
    if (!canAutoRemoveWorkoutSupportSnack(body, plan)) continue;

    const owner = plan.workoutSupportSnack
      ? {
          workoutPlanId: plan.workoutSupportSnack.workoutPlanId,
          timing: plan.workoutSupportSnack.timing,
        }
      : resolveSupportSnackOwner(body, plan);
    if (owner?.workoutPlanId === workoutPlanId) {
      ids.add(plan.id);
    }
  }
  return ids;
};

const supportSnackSlotMatchesWorkout = (
  slot: PlannerMealSlot,
  workout: PlannerWorkoutSlot,
): boolean => {
  const timing = slot.workoutSupportSnack?.timing;
  if (timing !== 'pre' && timing !== 'post') return false;
  return timing === 'pre'
    ? mealStartsBeforeWorkoutWithinHoursValue(
        slot.startTime,
        workout.startTime,
        PLANNER_STATIC_CONFIG.workoutProximityRules.closePreWorkoutWindowHours,
      )
    : mealStartsAfterWorkoutWithinMinutesValue(
        slot.startTime,
        workout.endTime,
        POST_WORKOUT_SNACK_WINDOW_MINUTES,
      );
};

const mealStartsBeforeWorkoutWithinHoursValue = (
  mealStartIso: string,
  workoutStartIso: string,
  hours: number,
): boolean =>
  isInPreWorkoutSupportWindow(
    Date.parse(mealStartIso),
    Date.parse(workoutStartIso),
    hours,
  );

const mealStartsAfterWorkoutWithinMinutesValue = (
  mealStartIso: string,
  workoutEndIso: string,
  minutes: number,
): boolean =>
  isInPostWorkoutSupportWindow(
    Date.parse(mealStartIso),
    Date.parse(workoutEndIso),
    minutes,
  );

const workoutUpdateAffectsSupportSnacks = (
  updates: Partial<DiaryMealPlan>,
  current: DiaryMealPlan,
): boolean =>
  (updates.plannedAt !== undefined && updates.plannedAt !== current.plannedAt) ||
  (updates.exerciseDurationMinutes !== undefined &&
    updates.exerciseDurationMinutes !== current.exerciseDurationMinutes) ||
  (updates.exerciseIntensity !== undefined &&
    updates.exerciseIntensity !== current.exerciseIntensity) ||
  (updates.exerciseCaloriesBurned !== undefined &&
    updates.exerciseCaloriesBurned !== current.exerciseCaloriesBurned) ||
  (updates.exerciseCarbsPerHour !== undefined &&
    updates.exerciseCarbsPerHour !== current.exerciseCarbsPerHour) ||
  (updates.exerciseCarbsConsumed !== undefined &&
    updates.exerciseCarbsConsumed !== current.exerciseCarbsConsumed) ||
  (updates.exerciseCaloriesConsumed !== undefined &&
    updates.exerciseCaloriesConsumed !== current.exerciseCaloriesConsumed) ||
  updates.periodization !== undefined;

const hasAnyMacroTotals = (totals: MacroTotals): boolean =>
  totals.calories > 0 || totals.protein > 0 || totals.carbs > 0 || totals.fat > 0;

const toPlannerMacroTarget = (
  totals: MacroTotals,
): {
  calories: number;
  proteinG: number;
  fatG: number;
  carbsG: number;
} => ({
  calories: Math.round(toNonNegativeNumber(totals.calories)),
  proteinG: Math.round(toNonNegativeNumber(totals.protein)),
  fatG: Math.round(toNonNegativeNumber(totals.fat)),
  carbsG: Math.round(toNonNegativeNumber(totals.carbs)),
});

const applyMealUserOverridesToPlannerTarget = (
  target: PlannerMealTargetOverride,
  plan: DiaryMeal,
): void => {
  const overrides = plan.custom?.target;
  if (!overrides) return;
  if (overrides.calories !== undefined) {
	    target.calories = {
	      value: Math.round(toNonNegativeNumber(overrides.calories)),
	      mode: 'locked',
	      source: 'user',
	    };
  }
  if (overrides.protein !== undefined) {
	    target.proteinG = {
	      value: Math.round(toNonNegativeNumber(overrides.protein)),
	      mode: 'locked',
	      source: 'user',
	    };
  }
  if (overrides.fat !== undefined) {
	    target.fatG = {
	      value: Math.round(toNonNegativeNumber(overrides.fat)),
	      mode: 'locked',
	      source: 'user',
	    };
  }
  if (overrides.carbs !== undefined) {
	    target.carbsG = {
	      value: Math.round(toNonNegativeNumber(overrides.carbs)),
	      mode: 'locked',
	      source: 'user',
	    };
  }
};

const applyLoggedMinimumsToPlannerTarget = (
  target: PlannerMealTargetOverride,
  totals: MacroTotals,
): void => {
  const minimum = toPlannerMacroTarget(totals);
  if (!target.calories) {
	    target.calories = {
	      value: minimum.calories,
	      mode: 'minimum',
	      source: 'logged',
	    };
  }
  if (!target.proteinG) {
	    target.proteinG = {
	      value: minimum.proteinG,
	      mode: 'minimum',
	      source: 'logged',
	    };
  }
  if (!target.fatG) {
	    target.fatG = {
	      value: minimum.fatG,
	      mode: 'minimum',
	      source: 'logged',
	    };
  }
  if (!target.carbsG) {
	    target.carbsG = {
	      value: minimum.carbsG,
	      mode: 'minimum',
	      source: 'logged',
	    };
  }
};

const resolveLoggedMealTargetForPlanner = (
  body: DiaryDayBody,
  slot: PlannerMealSlot,
  plan: DiaryMeal | null,
  isHistorical: boolean,
): NutritionPlannerRequest['loggedMeals'][number] | null => {
  if (plan?.completedAt || plan?.skippedAt) {
    const target = resolveMealTarget(plan) ?? emptyMealTarget();
    return {
      mealId: slot.key,
      ...toPlannerMacroTarget({
        calories: target.calories,
        protein: target.protein,
        fat: target.fat,
        carbs: target.carbs,
      }),
    };
  }

  if (!isHistorical) {
    return null;
  }

  const consumed = sumFoodEntriesForMealSlot(body, slot, plan);
  if (!hasAnyMacroTotals(consumed)) {
    return null;
  }

  return {
    mealId: slot.key,
    ...toPlannerMacroTarget(consumed),
  };
};

const hasPlannerMealTargetOverride = (target: PlannerMealTargetOverride): boolean =>
  !!(target.calories || target.proteinG || target.fatG || target.carbsG);

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value));

const addDaysToDateKey = (date: string, offset: number): string => {
  const [year, month, day] = date.split('-').map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return date;
  }
  const next = new Date(year, month - 1, day + offset);
  if (!Number.isFinite(next.getTime())) return date;
  return formatDateInput(next);
};

const resolvePlannerWindowDays = (): number => {
  const configuredDays = Math.round(Number(PLANNER_STATIC_CONFIG.rollingWindow.days));
  if (!Number.isFinite(configuredDays) || configuredDays < 1) {
    return 1;
  }
  return Math.round(clamp(configuredDays, 1, PLANNER_MAX_WINDOW_DAYS));
};

const resolvePlannerRollingWindowDays = (planningDays: number): number => {
  const configuredDays = Math.round(Number(PLANNER_STATIC_CONFIG.rollingWindow.days));
  if (!Number.isFinite(configuredDays) || configuredDays < 1) {
    return planningDays;
  }
  return Math.round(clamp(configuredDays, 1, planningDays));
};

const resolvePlannerHistoryDays = (planningDays: number): number =>
  Math.ceil(resolvePlannerRollingWindowDays(planningDays) / 2);

const buildPlannerContextDates = (
  startDate: string,
  planningDays: number,
  historyDays: number,
): string[] =>
  Array.from(
    { length: planningDays + historyDays },
    (_unused, index) => addDaysToDateKey(startDate, index - historyDays),
  );

const buildPlannerInputHash = (payload: unknown): string => {
  const serialized = JSON.stringify(payload);
  let hash = 2166136261;
  for (let i = 0; i < serialized.length; i += 1) {
    hash ^= serialized.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return `v1:${(hash >>> 0).toString(16).padStart(8, '0')}`;
};

// ---------------------------------------------------------------------------
// LRU cache — bounded at ~14 days
// ---------------------------------------------------------------------------

const CACHE_LIMIT = 14;

const LABEL_NUTRIENT_KEYS: Array<keyof NutritionLabelNutrients> = [
  'saturatedFatG',
  'transFatG',
  'cholesterolMg',
  'sodiumMg',
  'dietaryFiberG',
  'totalSugarsG',
  'addedSugarsG',
  'vitaminDMcg',
  'calciumMg',
  'ironMg',
  'potassiumMg',
];

const scaleLabelNutrients = (
  labelNutrients: NutritionLabelNutrients | undefined,
  amount: number,
): NutritionLabelNutrients | undefined => {
  if (!labelNutrients) return undefined;

  const scaled: NutritionLabelNutrients = {};
  let hasAny = false;

  for (const key of LABEL_NUTRIENT_KEYS) {
    const value = labelNutrients[key];
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    scaled[key] = Math.round(value * amount * 10) / 10;
    hasAny = true;
  }

  return hasAny ? scaled : undefined;
};

const scaleFoodServingNutrition = (serving: ServingSize, amount: number): Nutrition => ({
  calories: Math.round(serving.nutrition.calories * amount),
  protein: Math.round(serving.nutrition.protein * amount),
  carbs: Math.round(serving.nutrition.carbs * amount),
  fat: Math.round(serving.nutrition.fat * amount),
  labelNutrients: scaleLabelNutrients(serving.nutrition.labelNutrients, amount),
});

export const useDiaryStore = defineStore('diary', () => {
  const authStore = useAuthSessionStore();
  const diarySettingsStore = useDiarySettingsStore();
  const goalsStore = useGoalsStore();
  const weightStore = useWeightStore();
  const mealsStore = useMealsStore();

  const selectedDate = ref<string>(formatDateInput(new Date()));
  const dayCache = ref<Map<string, DiaryDay>>(new Map());
  const pendingFetches = new Map<string, Promise<DiaryDay | null>>();
  const isLoading = ref(false);

  const goalSets = ref<GoalSet[]>([]);
  const foodItems = ref<SyncableFoodItem[]>([]);
  const planningAddFoodTarget = ref<DiaryPlanningAddFoodTarget | null>(null);

  const hasLoadedGoalSets = ref(false);
  const hasLoadedFoodItems = ref(false);
  const hasLoadedCurrentDay = ref(false);

  let loadAllDataPromise: Promise<void> | null = null;

  const getUserId = (): string => {
    const userId = authStore.user?.id;
    if (!userId) {
      throw new Error('[diaryStore] No authenticated user');
    }
    return userId;
  };

  const isMealPlanningEnabled = async (): Promise<boolean> => {
    await diarySettingsStore.load();
    return diarySettingsStore.showMealPlanning;
  };

  const touchCache = (day: DiaryDay): void => {
    const cache = dayCache.value;
    if (cache.has(day.date)) {
      cache.delete(day.date);
    }
    cache.set(day.date, day);
    while (cache.size > CACHE_LIMIT) {
      const oldest = cache.keys().next().value;
      if (!oldest) break;
      cache.delete(oldest);
    }
    dayCache.value = new Map(cache);
  };

  const ensureDayLoaded = async (date: string): Promise<DiaryDay | null> => {
    const cached = dayCache.value.get(date);
    if (cached) return cached;
    const pending = pendingFetches.get(date);
    if (pending) return pending;

    const promise = (async () => {
      const userId = getUserId();
      const raw = await getDiaryDay(userId, date);
      if (!raw) return null;
      // Always deep-clone before mutating: backfillDiaryDay returns the same
      // reference when no backfill is needed, so assigning to day.body.meals
      // would otherwise mutate the object returned by the service layer.
      const day: DiaryDay = structuredClone(backfillDiaryDay(raw));
      if (await isMealPlanningEnabled()) {
        // Run meal planning algorithm to auto-create plans for enabled meals.
        await mealsStore.load();
        const result = recalculateMealPlans({
          body: day.body,
          selectedDate: date,
          meals: mealsStore.meals,
          now: new Date(),
        });
        day.body.meals = result.mealPlans;
      }
      touchCache(day);
      return day;
    })();
    pendingFetches.set(date, promise);
    try {
      return await promise;
    } finally {
      pendingFetches.delete(date);
    }
  };

  // ---------------------------------------------------------------------------
  // Current day + derived state
  // ---------------------------------------------------------------------------

  const currentDay = computed<DiaryDay | null>(
    () => dayCache.value.get(selectedDate.value) ?? null,
  );

  const currentBody = computed<DiaryDayBody | null>(() => currentDay.value?.body ?? null);

  const diaryEntries = computed<DiaryEntry[]>(() => {
    const body = currentBody.value;
    if (!body) return [];
    return sortedEntries(body);
  });

  const entriesForSelectedDate = diaryEntries;

  const foodEntriesForSelectedDate = computed<DiaryEntry[]>(() => {
    const body = currentBody.value;
    if (!body) return [];
    return foodEntriesOf(body);
  });

  const exerciseEntriesForSelectedDate = computed<DiaryEntry[]>(() => {
    const body = currentBody.value;
    if (!body) return [];
    return exerciseEntriesOf(body);
  });

  const exerciseCaloriesBurned = computed<number>(() => {
    const body = currentBody.value;
    if (!body) return 0;
    return computeExerciseCaloriesBurned(body);
  });

  const dailyTotals = computed<MacroTotals>(() => {
    const body = currentBody.value;
    if (!body) return emptyTotals();
    return computeFoodTotals(body);
  });

  const importedDailyTotal = computed<ImportedTotalCompat | null>(() => {
    const body = currentBody.value;
    if (!body?.importedTotals) return null;
    const now = currentDay.value?.updatedAt ?? new Date().toISOString();
    const createdAt = currentDay.value?.createdAt ?? now;
    return {
      id: IMPORTED_TOTAL_ID(selectedDate.value),
      date: selectedDate.value,
      importSource: body.importedTotals.importSource,
      calories: body.importedTotals.calories,
      protein: body.importedTotals.protein,
      carbs: body.importedTotals.carbs,
      fat: body.importedTotals.fat,
      labelNutrients: body.importedTotals.labelNutrients ?? null,
      notes: body.importedTotals.notes ?? null,
      createdAt,
      updatedAt: now,
      deletedAt: null,
    };
  });

  const hasImportedDailyTotal = computed(() => importedDailyTotal.value !== null);

  const effectiveDailyTotals = computed<MacroTotals>(() => {
    const body = currentBody.value;
    if (!body) return emptyTotals();
    return computeEffectiveTotals(body);
  });

  const mealTotalsById = computed(() => {
    const body = currentBody.value;
    if (!body) return new Map<MealId, MacroTotals>();
    return computeMealTotals(body);
  });

  const mealTotalsFor = computed(
    () => (mealId: MealId): MacroTotals =>
      mealTotalsById.value.get(mealId) ?? emptyTotals(),
  );

  const mealPlanTotalsById = computed(() => {
    const body = currentBody.value;
    if (!body) return new Map<string, MacroTotals>();
    return computeMealPlanTotals(body);
  });

  const mealPlanTotalsFor = computed(
    () => (mealPlanId: string): MacroTotals =>
      mealPlanTotalsById.value.get(mealPlanId) ?? emptyTotals(),
  );

  // planOverride was removed from the model; day-level goals come from
  // goalSnapshot and per-meal targets. Retained as a null-returning stub so
  // any lingering consumer keeps compiling.
  const dayPlanForSelectedDate = computed<DiaryDayPlanCompat | null>(() => null);

  const mealPlansForSelectedDate = computed<DiaryMealPlan[]>(() => {
    const body = currentBody.value;
    if (!body) return [];
    return sortedMealPlans(body).filter((plan) => !plan.deletedAt).map((plan) =>
      toMealPlanCompat(plan, selectedDate.value),
    );
  });

  const mealPlanGenerationState = computed<DiaryDayPlanGeneration>(() =>
    currentBody.value?.planGeneration ?? emptyDiaryDayPlanGeneration(),
  );

  const entriesForMealPlan = computed(
    () => (mealPlanId: string): DiaryEntry[] => {
      const body = currentBody.value;
      const meal = body?.meals.find((m) => m.id === mealPlanId && !m.deletedAt);
      if (!meal) return [];
      return meal.entries.filter((e) => e.source.type !== 'exercise');
    },
  );

  // Entries carry no mealId/mealPlanId of their own — the containing meal is the
  // sole association. Resolve an entry's meal routing from that container so
  // callers (e.g. the edit modal) can seed selectors and re-route on save.
  const mealContextForEntry = (
    entryId: string,
  ): { mealId: MealId | null; mealPlanId: string | null } | null => {
    for (const day of dayCache.value.values()) {
      const meal = day.body.meals.find(
        (m) => !m.deletedAt && m.entries.some((e) => e.id === entryId),
      );
      if (meal) {
        return {
          mealId: (meal.mealId ?? null) as MealId | null,
          mealPlanId: meal.id === UNSORTED_MEAL_ID ? null : meal.id,
        };
      }
    }
    return null;
  };

  const activeGoalSet = computed(() =>
    findActiveGoalSet(goalSets.value, selectedDate.value),
  );

  const resolveGoalTargetsForDay = (goal: Goal, date: string): DailyGoalTargets => {
    const weightKg = weightStore.getWeightForDay(date)?.weightKg ?? 70;
    return {
      calories: goal.caloriesTarget,
      ...goalsService.calculateDailyMacroTargets(goal, weightKg),
      ...(goal.flexMacro ? { flexMacro: goal.flexMacro } : {}),
    };
  };

  const resolveGoalSnapshotForDay = (date: string): DiaryDayGoalSnapshot | null => {
    const goal = goalsStore.getGoalForDay(date);
    if (!goal) return null;

    const targets = resolveGoalTargetsForDay(goal, date);
    return {
      goalType: goal.goalType,
      caloriesTarget: targets.calories,
      proteinG: targets.protein,
      carbsG: targets.carbs,
      fatG: targets.fat,
      ...(targets.flexMacro ? { flexMacro: targets.flexMacro } : {}),
      sourceGoalId: goal.id,
      resolvedAt: goal.updatedAtUtc,
    };
  };

  const syncGoalSnapshotForDay = (body: DiaryDayBody, date: string): void => {
    const snapshot = resolveGoalSnapshotForDay(date);
    if (snapshot) {
      body.goalSnapshot = snapshot;
    }
  };

  const dailyGoals = computed(() => {
    const body = currentBody.value;
    const bodyGoals = body ? resolveDailyGoals(body) : null;

    const liveGoal = goalsStore.getGoalForDay(selectedDate.value);
    if (liveGoal) return resolveGoalTargetsForDay(liveGoal, selectedDate.value);

    if (bodyGoals) return bodyGoals;

    // Fall back to legacy goalSet computation when the doc has no snapshot yet
    // (e.g. the day doc doesn't exist and the user hasn't logged anything)
    if (!activeGoalSet.value) return null;
    const { calories, macros } = activeGoalSet.value;
    if (macros.mode === 'grams') {
      return {
        calories,
        protein: macros.protein,
        carbs: macros.carbs,
        fat: macros.fat,
      };
    }
    const toGrams = (percent: number, caloriesPerGram: number) =>
      (percent / 100) * (calories / caloriesPerGram);
    return {
      calories,
      protein: toGrams(macros.protein, 4),
      carbs: toGrams(macros.carbs, 4),
      fat: toGrams(macros.fat, 9),
    };
  });

  const remainingSummary = computed(() => {
    const goals = dailyGoals.value;
    if (!goals) return null;
    const totals = effectiveDailyTotals.value;
    return {
      calories: goals.calories + exerciseCaloriesBurned.value - totals.calories,
      protein: goals.protein - totals.protein,
      carbs: goals.carbs - totals.carbs,
      fat: goals.fat - totals.fat,
    };
  });

  const hasLoadedData = computed(
    () => hasLoadedGoalSets.value && hasLoadedFoodItems.value && hasLoadedCurrentDay.value,
  );

  // ---------------------------------------------------------------------------
  // Loaders
  // ---------------------------------------------------------------------------

  const loadGoalSets = async (): Promise<void> => {
    const [loadedGoalSets] = await Promise.all([
      goalsService.getGoalSets(),
      goalsStore.loadGoalsHistory(),
      weightStore.ensureLoaded(),
    ]);
    goalSets.value = loadedGoalSets;
    hasLoadedGoalSets.value = true;
  };

  const loadFoodItems = async (): Promise<void> => {
    foodItems.value = await getFoodItems();
    hasLoadedFoodItems.value = true;
  };

  const loadCurrentDay = async (): Promise<void> => {
    await ensureDayLoaded(selectedDate.value);
    hasLoadedCurrentDay.value = true;
  };

  const loadAllData = async (): Promise<void> => {
    if (loadAllDataPromise) return loadAllDataPromise;
    loadAllDataPromise = (async () => {
      isLoading.value = true;
      try {
        // Start RxDB replication for diaryDays before loading data so that
        // the initial pull runs in parallel with other data fetches.
        const userId = authStore.user?.id;
        if (userId) {
          const { ensureReplication } = await import('@/services/rxdb/replication');
          void ensureReplication(userId);
        }
        await Promise.all([loadGoalSets(), loadFoodItems(), loadCurrentDay()]);
      } finally {
        isLoading.value = false;
        loadAllDataPromise = null;
      }
    })();
    return loadAllDataPromise;
  };

  const ensureDataLoaded = async (): Promise<void> => {
    if (hasLoadedData.value) return;
    await loadAllData();
  };

  /**
   * RxDB subscription for the currently-selected diary day.
   *
   * Whenever `selectedDate` or the authenticated user changes, we subscribe to
   * the RxDB document for that date. Any local mutation or remote replication
   * write fires the callback synchronously, keeping `dayCache` fresh without
   * manual cache invalidation.
   */
  watchEffect((onCleanup) => {
    const date = selectedDate.value;
    const userId = authStore.user?.id;
    if (!userId) return;

    const unsub = subscribeToDay(userId, date, (day) => {
      if (day) {
        // Normalize legacy (pre-`meals`) bodies before caching. Days pulled
        // from the server via replication still arrive in the old shape (the
        // backend stores the raw body), so this reactive path must run the same
        // defensive backfill as `ensureDayLoaded` — otherwise `body.meals` is
        // undefined and readers throw "body.meals is not iterable".
        touchCache(backfillDiaryDay(day));
      } else {
        dayCache.value.delete(date);
        dayCache.value = new Map(dayCache.value);
      }
    });
    onCleanup(unsub);
  });

  /**
   * No-op: RxDB subscriptions keep the cache fresh automatically.
   * Retained for backward compatibility with the invalidation hook in main.ts
   * until Phase 8 cleanup.
   */
  const invalidateAfterSync = async (): Promise<void> => {
    // goalSets and foodItems still use the legacy service layer; reload them.
    hasLoadedGoalSets.value = false;
    hasLoadedFoodItems.value = false;
    hasLoadedCurrentDay.value = false;
    loadAllDataPromise = null;
    await loadAllData();
  };

  const setSelectedDate = async (date: string): Promise<void> => {
    selectedDate.value = date;
    await ensureDayLoaded(date);
  };

  const plannerRunQueueByDate = new Map<string, Promise<void>>();

  const parseClockTime = (value: string | undefined): { hours: number; minutes: number } | null => {
    if (!value) return null;
    const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value.trim());
    if (!match) return null;
    return { hours: Number(match[1]), minutes: Number(match[2]) };
  };

  const clockFromMinutes = (totalMinutes: number): string => {
    const normalized = ((totalMinutes % (24 * 60)) + (24 * 60)) % (24 * 60);
    const hours = Math.floor(normalized / 60);
    const minutes = normalized % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  const fallbackMealStartTime = (displayOrder: number): string =>
    clockFromMinutes(5 * 60 + Math.max(0, displayOrder) * 120);

  const resolveMealWindow = (
    date: string,
    startTime: string | undefined,
    endTime: string | undefined,
    displayOrder: number,
  ): { startIso: string; endIso: string } => {
    const fallbackStart = fallbackMealStartTime(displayOrder);
    const startClock = parseClockTime(startTime) ? startTime! : fallbackStart;
    const startIso = toUtcIsoFromLocalDateTime(date, startClock);
    if (parseClockTime(endTime)) {
      let endIso = toUtcIsoFromLocalDateTime(date, endTime!);
      if (new Date(endIso).getTime() <= new Date(startIso).getTime()) {
        endIso = addMinutesIso(endIso, 24 * 60);
      }
      return { startIso, endIso };
    }
    return {
      startIso,
      endIso: addMinutesIso(startIso, DEFAULT_MEAL_WINDOW_MINUTES),
    };
  };

  const resolveDailyGoalsForDate = (body: DiaryDayBody, date: string): DailyGoalTargets | null => {
    const bodyGoals = resolveDailyGoals(body);

    const liveGoal = goalsStore.getGoalForDay(date);
    if (liveGoal) return resolveGoalTargetsForDay(liveGoal, date);

    if (bodyGoals) return bodyGoals;

    const goalSet = findActiveGoalSet(goalSets.value, date);
    if (!goalSet) return null;

    const { calories, macros } = goalSet;
    if (macros.mode === 'grams') {
      return {
        calories,
        protein: macros.protein,
        carbs: macros.carbs,
        fat: macros.fat,
      };
    }

    return {
      calories,
      protein: (macros.protein / 100) * (calories / 4),
      carbs: (macros.carbs / 100) * (calories / 4),
      fat: (macros.fat / 100) * (calories / 9),
    };
  };

  const resolvePlannerWorkoutSlots = (
    body: DiaryDayBody,
    date: string,
    isSelectedDay: boolean,
    isHistorical = false,
  ): PlannerWorkoutSlot[] => {
    return body.meals
      .filter((plan) => !plan.deletedAt)
      .filter((plan) => resolvePlanKind(plan) !== 'meal')
      .filter((plan) => !isHistorical || hasRecordedActualBurn(plan))
      .map((plan) => {
        const exerciseOverrides = plan.custom?.exercise;
        // Per-field fallback, matching what the workout card shows: each field
        // uses the user's edit when present, else the planned value. (Treating
        // any edit as replacing the whole planned exercise made logging fuel
        // alone zero out the workout's burn and duration.)
        const exercise = resolveMealExercise(plan);
        // Fuel falls back the same way, except a planned layer that merely
        // echoes the planner's own previous fueling answer is not user input.
        const plannedFuel =
          plan.planned.exercise && !hasPlannerGeneratedFuelInExercise(plan, plan.planned.exercise)
            ? plan.planned.exercise
            : null;
        const pickFuel = (key: 'carbsPerHour' | 'carbsConsumed' | 'caloriesConsumed') =>
          exerciseOverrides && key in exerciseOverrides
            ? exerciseOverrides[key]
            : plannedFuel?.[key];
        const duration = resolveWorkoutDurationMinutes(plan);
        const startTime = resolveMealPlannedAt(plan);
        const endTime = addMinutesIso(resolveMealPlannedAt(plan), duration);
        // A past workout with actuals is done by definition: always send it as
        // completed so the planner treats it as accounting-only (no fueling or
        // pre/post-workout targets against already-logged past meals).
        const completedAt =
          plan.workoutActuals?.completedAt ??
          plan.completedAt ??
          (isHistorical ? endTime : undefined);
        const supportSnackSuppression = resolveWorkoutSupportSnackSuppression(
          body,
          plan,
          startTime,
          endTime,
        );
        const caloriesBurned =
          toOptionalNonNegativeNumber(plan.workoutActuals?.caloriesBurned) ??
          toOptionalNonNegativeNumber(exercise?.caloriesBurned) ??
          0;
        const providedCarbsPerHour =
          toOptionalPlannerNumber(plan.workoutActuals?.fuelCarbsPerHour) ??
          toOptionalPlannerNumber(pickFuel('carbsPerHour'));
        const actualFuelCalories = toOptionalPlannerNumber(plan.workoutActuals?.fuelCalories);
        const actualFuelCarbs = toOptionalPlannerNumber(plan.workoutActuals?.fuelCarbs);
        const providedFuelCalories = toOptionalPlannerNumber(pickFuel('caloriesConsumed'));
        const providedFuelCarbs = toOptionalPlannerNumber(pickFuel('carbsConsumed'));
        // Carbs/hour is only a client-side display representation; the planner's
        // authoritative fueling input is total carbs. Resolve the total here
        // (explicit carbsConsumed, else rate * duration) and send only that.
        // Sending both a rate and a total made the solver stack two conflicting
        // equality bounds on workout carbs (rounding-apart), stalling OSQP.
        const totalFuelCarbs =
          providedFuelCarbs ??
          (providedCarbsPerHour !== undefined
            ? Math.round((providedCarbsPerHour * duration) / 60)
            : undefined);
        const fuelingTargetOverride: PlannerWorkoutSlot['fuelingTargetOverride'] = {};
        if (!completedAt && providedFuelCalories !== undefined) {
          fuelingTargetOverride.calories = {
            value: Math.round(providedFuelCalories),
            mode: 'locked',
            source: 'user',
          };
        }
        if (!completedAt && totalFuelCarbs !== undefined) {
          fuelingTargetOverride.carbsG = {
            value: Math.round(totalFuelCarbs),
            mode: 'locked',
            source: 'user',
          };
        }
        return {
          workoutId: plannerScopedKey(date, isSelectedDay, `workout-${plan.id}`),
          fuelingId: plannerScopedKey(date, isSelectedDay, `fuel-${plan.id}`),
          date,
          planId: plan.id,
          isSelectedDay,
          startTime,
          endTime,
          intensity: mapExerciseIntensityToPlanner(
            plan.workoutActuals?.intensity ?? exercise?.intensity ?? null,
          ),
          caloriesBurned,
          completedAt,
          carbsPerHourDuringWorkout: completedAt ? undefined : providedCarbsPerHour,
          actualFuelCalories: completedAt ? actualFuelCalories : undefined,
          actualFuelCarbsG: completedAt ? actualFuelCarbs : undefined,
          fuelingTargetOverride: hasPlannerMealTargetOverride(fuelingTargetOverride)
            ? fuelingTargetOverride
            : undefined,
          suppressPreWorkoutSnack: supportSnackSuppression.preWorkout === true,
          suppressPostWorkoutSnack: supportSnackSuppression.postWorkout === true,
        };
      })
      .filter(
        (slot) =>
          slot.caloriesBurned > 0 ||
          new Date(slot.endTime).getTime() > new Date(slot.startTime).getTime(),
      );
  };

  const resolvePlannerMealSlots = (
    body: DiaryDayBody,
    date: string,
    generatedForDay: boolean,
    isSelectedDay: boolean,
  ): PlannerMealSlot[] => {
    const keyPrefix = isSelectedDay ? '' : `${date}:`;
    const enabledMeals = mealsStore.meals
      .filter((meal) => meal.enabled)
      .slice()
      .sort((a, b) => a.displayOrder - b.displayOrder);

    const activePlans = body.meals.filter((plan) => !plan.deletedAt);
    const deletedMealIds = new Set(
      body.meals
        .filter((plan) => !!plan.deletedAt && plan.mealId !== null)
        .map((plan) => plan.mealId as MealId),
    );
    const mealPlansByMealId = new Map(
      activePlans
        .filter((plan) => plan.mealId !== null && resolvePlanKind(plan) === 'meal')
        .map((plan) => [plan.mealId as MealId, plan]),
    );

    const configuredMealSlots: PlannerMealSlot[] = enabledMeals.reduce<PlannerMealSlot[]>(
      (slots, meal) => {
        const existing = mealPlansByMealId.get(meal.id) ?? null;
        if (!existing && (generatedForDay || deletedMealIds.has(meal.id))) {
          return slots;
        }

        const window = resolveMealWindow(
          date,
          meal.startTime,
          meal.endTime,
          meal.displayOrder,
        );

        slots.push({
          key: `${keyPrefix}meal-${meal.id}`,
          date,
          mealId: meal.id,
          title: existing ? resolveMealTitle(existing) : meal.title,
          mealType: inferMealType(meal.title),
          startTime: existing ? resolveMealPlannedAt(existing) : window.startIso,
          requestTime: window.startIso,
          endTime: window.endIso,
          existingPlanId: existing?.id ?? null,
        });
        return slots;
      },
      [],
    );

    const adHocMealSlots: PlannerMealSlot[] = activePlans
      .filter((plan) => plan.mealId === null && resolvePlanKind(plan) === 'meal')
      .map((plan) => ({
        key: `${keyPrefix}adhoc-${plan.id}`,
        date,
        mealId: null,
        title: resolveMealTitle(plan) || 'Meal',
        mealType: inferMealType(resolveMealTitle(plan) || 'Meal'),
        startTime: resolveMealPlannedAt(plan),
        requestTime: resolveMealPlannedAt(plan),
        endTime: addMinutesIso(resolveMealPlannedAt(plan), DEFAULT_MEAL_WINDOW_MINUTES),
        existingPlanId: plan.id,
        workoutSupportSnack: plan.workoutSupportSnack ?? null,
        canAutoRemoveWorkoutSupportSnack: canAutoRemoveWorkoutSupportSnack(body, plan),
      }));

    return [...configuredMealSlots, ...adHocMealSlots].sort((a, b) =>
      a.startTime.localeCompare(b.startTime),
    );
  };

  const mealStartsBeforeWorkoutWithinHours = (
    slot: PlannerMealSlot,
    workout: PlannerWorkoutSlot,
    hours: number,
  ): boolean => {
    const mealStart = new Date(slot.startTime).getTime();
    const workoutStart = new Date(workout.startTime).getTime();
    return isInPreWorkoutSupportWindow(mealStart, workoutStart, hours);
  };

  const mealStartsAfterWorkoutWithinMinutes = (
    slot: PlannerMealSlot,
    workout: PlannerWorkoutSlot,
    minutes: number,
  ): boolean => {
    const mealStart = new Date(slot.startTime).getTime();
    const workoutEnd = new Date(workout.endTime).getTime();
    return isInPostWorkoutSupportWindow(mealStart, workoutEnd, minutes);
  };

  const workoutSupportMealSlots = (
    baseSlots: PlannerMealSlot[],
    workouts: PlannerWorkoutSlot[],
  ): PlannerMealSlot[] => {
    const workoutsByPlanId = new Map(workouts.map((workout) => [workout.planId, workout]));
    const next = baseSlots.filter((slot) => {
      if (!slot.workoutSupportSnack || !slot.canAutoRemoveWorkoutSupportSnack) {
        return true;
      }
      const workout = workoutsByPlanId.get(slot.workoutSupportSnack.workoutPlanId);
      return workout ? supportSnackSlotMatchesWorkout(slot, workout) : false;
    });
    // Automatic pre/post-workout meal insertion is temporarily disabled.
    if (ADD_WORKOUT_SUPPORT_MEALS) {
      const closePreHours = PLANNER_STATIC_CONFIG.workoutProximityRules.closePreWorkoutWindowHours;

      for (const workout of workouts) {
        const hasPreWorkoutSnack = next.some((slot) =>
          mealStartsBeforeWorkoutWithinHours(slot, workout, closePreHours),
        );
        if (!workout.suppressPreWorkoutSnack && !hasPreWorkoutSnack) {
          const startTime = addMinutesIso(workout.startTime, -PRE_WORKOUT_MEAL_BEFORE_MINUTES);
          next.push({
            key: plannerScopedKey(workout.date, workout.isSelectedDay, `pre-workout-snack-${workout.planId}`),
            date: workout.date,
            mealId: null,
            title: 'Pre-workout snack',
            mealType: 'snack',
            startTime,
            requestTime: startTime,
            endTime: addMinutesIso(startTime, DEFAULT_MEAL_WINDOW_MINUTES),
            existingPlanId: null,
            workoutSupportSnack: {
              workoutPlanId: workout.planId,
              timing: 'pre',
            },
          });
        }

        const hasPostWorkoutSnack = next.some((slot) =>
          mealStartsAfterWorkoutWithinMinutes(slot, workout, POST_WORKOUT_SNACK_WINDOW_MINUTES),
        );
        if (!workout.suppressPostWorkoutSnack && !hasPostWorkoutSnack) {
          const startTime = addMinutesIso(workout.endTime, POST_WORKOUT_SNACK_WINDOW_MINUTES);
          next.push({
            key: plannerScopedKey(workout.date, workout.isSelectedDay, `post-workout-snack-${workout.planId}`),
            date: workout.date,
            mealId: null,
            title: 'Post-workout snack',
            mealType: 'snack',
            startTime,
            requestTime: startTime,
            endTime: addMinutesIso(startTime, DEFAULT_MEAL_WINDOW_MINUTES),
            existingPlanId: null,
            workoutSupportSnack: {
              workoutPlanId: workout.planId,
              timing: 'post',
            },
          });
        }
      }
    }

    return next.sort((a, b) => a.startTime.localeCompare(b.startTime));
  };

  const loadPlannerWindowContexts = async (
    date: string,
    selectedBody: DiaryDayBody,
    planningDays: number,
  ): Promise<PlannerWindowContext[]> => {
    const windowDates = buildPlannerContextDates(
      date,
      planningDays,
      resolvePlannerHistoryDays(planningDays),
    );
    if (windowDates.length === 1) {
      return [{ date, body: selectedBody, isSelectedDay: true, isHistorical: false }];
    }

    const userId = getUserId();
    const rangeDays = await getDiaryDaysInRange(
      userId,
      windowDates[0],
      windowDates[windowDates.length - 1],
    );
    const daysByDate = new Map(
      rangeDays.map((day) => [day.date, backfillDiaryDay(day).body] as const),
    );

    return windowDates.map((windowDate) => ({
      date: windowDate,
      body:
        windowDate === date
          ? selectedBody
          : structuredClone(daysByDate.get(windowDate) ?? emptyDiaryDayBody()),
      isSelectedDay: windowDate === date,
      isHistorical: windowDate < date,
    }));
  };

  const buildPlannerRequestContext = async (
    body: DiaryDayBody,
    date: string,
  ): Promise<{
    request: NutritionPlannerRequest | null;
    mealSlots: PlannerMealSlot[];
    inputHash: string | null;
  }> => {
    const planningDays = resolvePlannerWindowDays();
    const windowContexts = await loadPlannerWindowContexts(date, body, planningDays);
    const goals = resolveDailyGoalsForDate(body, date);
    // Historical days contribute their logged food to the rolling balance, so
    // they must contribute their recorded workout burn too — otherwise a past
    // training day reads as a large surplus and squeezes today's targets.
    // Historical slots are always completed (they carry actuals), so the planner
    // treats them as accounting-only and returns no targets for past days.
    const workouts = windowContexts.flatMap((context) =>
      resolvePlannerWorkoutSlots(
        context.body,
        context.date,
        context.isSelectedDay,
        context.isHistorical,
      ),
    );
    const hashPayload = {
      date,
      planningDays,
      goals,
      enabledMeals: mealsStore.meals
        .filter((meal) => meal.enabled)
        .slice()
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((meal) => ({
          id: meal.id,
          displayOrder: meal.displayOrder,
          title: meal.title,
          mealType: inferMealType(meal.title),
          time: meal.startTime ?? null,
        })),
      workouts: workouts.map((workout) => ({
        workoutId: workout.workoutId,
        startTime: workout.startTime,
        endTime: workout.endTime,
        intensity: workout.intensity,
        caloriesBurned: workout.caloriesBurned,
        completedAt: workout.completedAt ?? null,
        actualFuelCalories: workout.actualFuelCalories ?? null,
        actualFuelCarbsG: workout.actualFuelCarbsG ?? null,
        providedCarbsPerHourDuringWorkout: workout.carbsPerHourDuringWorkout ?? null,
        suppressPreWorkoutSnack: workout.suppressPreWorkoutSnack,
        suppressPostWorkoutSnack: workout.suppressPostWorkoutSnack,
      })),
    };
    const inputHash = buildPlannerInputHash(hashPayload);

    if (!goals) {
      return { request: null, mealSlots: [], inputHash };
    }

    const workoutsByDate = workouts.reduce<Map<string, PlannerWorkoutSlot[]>>((groups, workout) => {
      const group = groups.get(workout.date) ?? [];
      group.push(workout);
      groups.set(workout.date, group);
      return groups;
    }, new Map());
    const allMealSlots = windowContexts
      .flatMap((context) => {
        const resolvedSlots = resolvePlannerMealSlots(
          context.body,
          context.date,
          context.body.planGeneration.generatedForDay,
          context.isSelectedDay,
        );
        const slotsWithWorkoutSupport = context.isHistorical
          ? resolvedSlots
          : workoutSupportMealSlots(resolvedSlots, workoutsByDate.get(context.date) ?? []);
        return slotsWithWorkoutSupport.filter((slot) => {
          if (!context.isHistorical) {
            return true;
          }
          const plan = slot.existingPlanId
            ? context.body.meals.find((item) => item.id === slot.existingPlanId) ?? null
            : null;
          return resolveLoggedMealTargetForPlanner(context.body, slot, plan, true) !== null;
        });
      })
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
    const mealSlots = allMealSlots.filter((slot) => slot.date >= date);
    const selectedDaySlots = mealSlots.filter((slot) => slot.date === date);
    if (selectedDaySlots.length === 0) {
      return { request: null, mealSlots, inputHash };
    }

    const bodyByDate = new Map(
      windowContexts.map((context) => [context.date, context.body] as const),
    );
    const contextByDate = new Map(windowContexts.map((context) => [context.date, context] as const));
    const persistedMealInputs = allMealSlots.reduce<{
      loggedMeals: NutritionPlannerRequest['loggedMeals'];
      mealOverrides: NutritionPlannerRequest['mealOverrides'];
    }>((inputs, slot) => {
      const contextBody = bodyByDate.get(slot.date);
      const context = contextByDate.get(slot.date);
      if (!contextBody || !context) return inputs;
      const plan = slot.existingPlanId
        ? contextBody.meals.find((item) => item.id === slot.existingPlanId) ?? null
        : null;

      const loggedMeal = resolveLoggedMealTargetForPlanner(
        contextBody,
        slot,
        plan,
        context.isHistorical,
      );
      if (loggedMeal) {
        inputs.loggedMeals.push(loggedMeal);
        return inputs;
      }

      if (!plan || context.isHistorical) return inputs;

      const targetOverride: PlannerMealTargetOverride = {};
      applyMealUserOverridesToPlannerTarget(targetOverride, plan);
      const consumed = sumFoodEntriesForPlan(plan);
      if (hasAnyMacroTotals(consumed)) {
        applyLoggedMinimumsToPlannerTarget(targetOverride, consumed);
      }
      if (hasPlannerMealTargetOverride(targetOverride)) {
        inputs.mealOverrides.push({
          mealId: slot.key,
          target: targetOverride,
        });
      }
      return inputs;
    }, { loggedMeals: [], mealOverrides: [] });
    const { loggedMeals, mealOverrides } = persistedMealInputs;

    const bodyWeightKg = Math.max(35, weightStore.getWeightForDay(date)?.weightKg ?? 70);
    const restingCalories = Math.max(800, Math.round(goals.calories * 0.7));
    const nonExerciseActivityCalories = Math.max(0, Math.round(goals.calories - restingCalories));
    const proteinPerKg = clamp(goals.protein / bodyWeightKg, 0.7, 3.5);
    const fatPerKg = clamp(goals.fat / bodyWeightKg, 0.3, 2.2);
    const mappedGoal: NutritionPlannerRequest['user']['goal'] =
      body.goalSnapshot?.goalType === 'lose' || body.goalSnapshot?.goalType === 'gain'
        ? body.goalSnapshot.goalType
        : 'maintain';

    const request: NutritionPlannerRequest = {
      planningStart: toUtcIsoFromLocalDateTime(date, '00:00'),
      planningStartDate: date,
      planningDays,
      user: {
        bodyWeightKg,
        dailyCalories: restingCalories + nonExerciseActivityCalories,
        goal: mappedGoal,
      },
      config: {
        ...PLANNER_STATIC_CONFIG,
        macroTargets: {
          proteinGPerKgBodyWeight: proteinPerKg,
          fatGPerKgBodyWeight: fatPerKg,
        },
      },
      meals: allMealSlots.map((slot) => ({
        id: slot.key,
        time: slot.requestTime,
        date: slot.date,
        mealType: slot.mealType,
      })),
      duringWorkoutFueling: workouts.map((workout) => ({
        id: workout.fuelingId,
        workoutId: workout.workoutId,
        startTime: workout.startTime,
        endTime: workout.endTime,
        date: workout.date,
        ...(workout.actualFuelCalories !== undefined
          ? { actualCalories: workout.actualFuelCalories }
          : {}),
        ...(workout.actualFuelCarbsG !== undefined
          ? { actualCarbsG: workout.actualFuelCarbsG }
          : {}),
        // carbsPerHour is intentionally omitted: it is a display-only rate. The
        // authoritative total is sent via targetOverride.carbsG (see builder).
        ...(workout.fuelingTargetOverride ? { targetOverride: workout.fuelingTargetOverride } : {}),
      })),
      workouts: workouts.map((workout) => ({
        id: workout.workoutId,
        startTime: workout.startTime,
        endTime: workout.endTime,
        date: workout.date,
        intensity: workout.intensity,
        caloriesBurned: workout.caloriesBurned,
        suppressPreWorkoutSnack: workout.suppressPreWorkoutSnack,
        suppressPostWorkoutSnack: workout.suppressPostWorkoutSnack,
        ...(workout.completedAt ? { completedAt: workout.completedAt } : {}),
      })),
      loggedMeals,
      mealOverrides,
    };

    return { request, mealSlots, inputHash };
  };

  const runPlannerGeneration = async (
    date: string,
    source: string,
  ): Promise<void> => {
    await mealsStore.load();

    const preparedDay = await applyAndCache(date, (body) => {
      if (!body.planGeneration) {
        body.planGeneration = emptyDiaryDayPlanGeneration();
      }
    });
    const initialGeneration = preparedDay.body.planGeneration ?? emptyDiaryDayPlanGeneration();
    const initialContext = await buildPlannerRequestContext(preparedDay.body, date);
    const inputHash = initialContext.inputHash;
    const inputsChanged = initialGeneration.lastInputHash !== inputHash;

    if (inputsChanged && initialGeneration.generatedForDay) {
      await applyAndCache(date, (body) => {
        body.planGeneration = {
          ...body.planGeneration,
          generatedForDay: false,
          generatedAt: null,
          plannerDiagnostics: null,
        };
      });
    }

    const latestDay = (await ensureDayLoaded(date)) ?? preparedDay;
    const context = await buildPlannerRequestContext(latestDay.body, date);

    if (!context.request || context.mealSlots.length === 0) {
      await applyAndCache(date, (body) => {
        body.planGeneration = {
          ...body.planGeneration,
          status: 'idle',
          errorMessage: null,
          plannerDiagnostics: null,
          ...(context.inputHash ? { lastInputHash: context.inputHash } : {}),
        };
      });
      return;
    }

    await applyAndCache(date, (body) => {
      body.planGeneration = {
        ...body.planGeneration,
        status: 'pending',
        errorMessage: null,
      };
    });

    try {
	      const response = await nutritionPlannerClient.generatePlan(context.request);

      if (response.status === 'conflict' || response.status === 'error') {
        const message =
          response.status === 'conflict'
            ? response.conflicts.map((conflict: { message: string }) => conflict.message).join('; ')
            : response.error.message;
        await applyAndCache(date, (body) => {
          body.planGeneration = {
            ...body.planGeneration,
            status: 'error',
            errorMessage: message || 'Unable to generate meal targets.',
            plannerDiagnostics: response.diagnostics ?? null,
          };
        });
        return;
      }

      const targetByMealId = new Map<
        string,
        PlannerMealTarget
      >(
        response.mealTargets.map((target) => [target.mealId, target] as const),
      );
      const workoutTargetsByDate = response.duringWorkoutTargets.reduce<
        Map<string, PlannerWorkoutTargetRef[]>
      >((groups, target) => {
        const ref = parsePlannerWorkoutTargetRef(target.workoutId, date, target);
        if (!ref) return groups;
        const group = groups.get(ref.date) ?? [];
        group.push(ref);
        groups.set(ref.date, group);
        return groups;
      }, new Map());
      const nowIso = new Date().toISOString();
      const slotsByDate = context.mealSlots.reduce<Map<string, PlannerMealSlot[]>>(
        (groups, slot) => {
          if (!targetByMealId.has(slot.key)) return groups;
          const group = groups.get(slot.date) ?? [];
          group.push(slot);
          groups.set(slot.date, group);
          return groups;
        },
        new Map(),
      );

      const applyPlannerTargetsForDate = async (
        targetDate: string,
        slots: PlannerMealSlot[],
        workoutTargets: PlannerWorkoutTargetRef[],
      ): Promise<void> => {
        await applyAndCache(targetDate, (body) => {
          const mealPlansById = new Map(body.meals.map((plan) => [plan.id, plan]));
          // `slot.existingPlanId` is captured from the body snapshot used to build the
          // planner request, before the (possibly slow) network round trip. If another
          // generation run committed new meal plans for this date in the meantime, that
          // snapshot is stale and existingPlanId can be null even though a plan for this
          // mealId now exists — which would otherwise fall through to the create branch
          // below and insert a duplicate meal. Re-resolve against the fresh body by
          // mealId so an existing plan is always found and updated in place.
          const mealPlansByMealId = new Map(
            body.meals
              .filter((plan) => !plan.deletedAt && plan.mealId !== null && resolvePlanKind(plan) === 'meal')
              .map((plan) => [plan.mealId as MealId, plan]),
          );
          const deletedMealIds = new Set(
            body.meals
              .filter((plan) => plan.deletedAt && plan.mealId !== null && resolvePlanKind(plan) === 'meal')
              .map((plan) => plan.mealId as MealId),
          );
          const mealPlanIdByMealId = new Map<MealId, string>();
          const slotPlanIds = new Set<string>();
          const slotMealIds = new Set<MealId>();
          for (const slot of slots) {
            if (slot.existingPlanId !== null) slotPlanIds.add(slot.existingPlanId);
            if (slot.mealId !== null) slotMealIds.add(slot.mealId);
          }
          const nowIsoInner = new Date().toISOString();
          const updatedPlans = body.meals.map((plan) => {
            if (plan.deletedAt) return plan;
            const isPlannerGenerated = plan.plannerResult?.mealTarget != null;
            if (!isPlannerGenerated) return plan;
            if (plan.completedAt || isMealCustomized(plan)) return plan;
            const stillReferenced = slotPlanIds.has(plan.id) || (plan.mealId !== null && slotMealIds.has(plan.mealId as MealId));
            if (stillReferenced) return plan;
            return { ...plan, deletedAt: nowIsoInner };
          });
          const generation = body.planGeneration ?? emptyDiaryDayPlanGeneration();

          for (const slot of slots) {
            const target = targetByMealId.get(slot.key);
            if (!target) continue;

            const existingPlan =
              (slot.mealId !== null ? mealPlansByMealId.get(slot.mealId) : null) ??
              (slot.existingPlanId && mealPlansById.get(slot.existingPlanId)) ??
              null;
            const nextTarget = {
              calories: Math.round(toNonNegativeNumber(target.calories)),
              protein: Math.round(toNonNegativeNumber(target.proteinG)),
              carbs: Math.round(toNonNegativeNumber(target.carbsG)),
              fat: Math.round(toNonNegativeNumber(target.fatG)),
            };

            if (existingPlan) {
              // The planner writes the `planned` baseline; any `custom` override
              // still wins at read time, so no merge is needed here.
              //
              // A completed or skipped meal is the one exception: `completeDiaryMealPlan`
              // locks that meal's target to what was actually eaten by writing it to
              // `planned.target` AND deleting `custom.target`, so those actuals sit in
              // the very field this write replaces, with no `custom` layer to protect
              // them. Overwriting them (e.g. from a planner response built before the
              // meal was completed) silently restated how much the user ate, and the
              // next run reads those numbers back as the logged amount — under-counting
              // the day and pushing the phantom calories onto whichever meal was still
              // open, which is how a Bedtime Snack ended up pinned at its 1500 kcal max.
              const isSettled = !!(existingPlan.completedAt || existingPlan.skippedAt);
              const existingIndex = updatedPlans.findIndex((item) => item.id === existingPlan.id);
              if (existingIndex === -1) continue;
              updatedPlans[existingIndex] = {
                ...existingPlan,
                planned: {
                  ...existingPlan.planned,
                  title: existingPlan.planned.title || slot.title,
                  plannedAt: existingPlan.planned.plannedAt || slot.startTime,
                  target: isSettled ? existingPlan.planned.target : nextTarget,
                },
                kind: existingPlan.kind ?? 'meal',
                periodization: existingPlan.periodization ?? defaultPeriodization(),
                plannerResult: {
                  ...existingPlan.plannerResult,
                  mealTarget: {
                    calories: target.calories,
                    proteinG: target.proteinG,
                    carbsG: target.carbsG,
                    fatG: target.fatG,
                    reasonCodes: target.reasonCodes,
                    confidence: target.confidence,
                  },
                },
              };
              if (slot.mealId !== null) {
                mealPlanIdByMealId.set(slot.mealId, existingPlan.id);
              }
              continue;
            }

            // A different run/device may have created and deleted this slot
            // while the request was in flight. Respect the fresh tombstone.
            if (generation.generatedForDay || (slot.mealId !== null && deletedMealIds.has(slot.mealId))) {
              continue;
            }

            const created: DiaryMeal = {
              id: slot.mealId !== null
                ? configuredMealPlanId(targetDate, slot.mealId)
                : generateId(),
              mealId: slot.mealId,
              kind: 'meal',
              planned: {
                title: slot.title,
                plannedAt: slot.startTime,
                target: nextTarget,
                exercise: null,
              },
              entries: [],
              workoutActuals: null,
              workoutSupportSnack: slot.workoutSupportSnack ?? null,
              plannerResult: {
                mealTarget: {
                  calories: target.calories,
                  proteinG: target.proteinG,
                  carbsG: target.carbsG,
                  fatG: target.fatG,
                  reasonCodes: target.reasonCodes,
                  confidence: target.confidence,
                },
              },
              periodization: defaultPeriodization(),
              completedAt: null,
              skippedAt: null,
              deletedAt: null,
            };
            updatedPlans.push(created);
            mealPlansById.set(created.id, created);
            if (slot.mealId !== null) {
              mealPlansByMealId.set(slot.mealId, created);
              mealPlanIdByMealId.set(slot.mealId, created.id);
            }
          }

          for (const workoutTarget of workoutTargets) {
            const existingIndex = updatedPlans.findIndex((item) => item.id === workoutTarget.planId);
            if (existingIndex === -1) continue;
            const existingPlan = updatedPlans[existingIndex];
            updatedPlans[existingIndex] = {
              ...existingPlan,
              plannerResult: {
                ...existingPlan.plannerResult,
                workoutFuelingTarget: {
                  calories: workoutTarget.target.calories,
                  carbsG: workoutTarget.target.carbsG,
                  carbsPerHour: workoutTarget.target.carbsPerHour,
                  source: workoutTarget.target.source,
                  reasonCodes: workoutTarget.target.reasonCodes,
                },
                underFuelingTargeted: isUnderFuelingTarget(workoutTarget.target),
              },
            };
          }

          body.meals = updatedPlans;
          // Entries live inside their meal, so no cross-meal reassignment is
          // needed when the planner materializes plans.
          void mealPlanIdByMealId;
          body.planGeneration = {
            ...body.planGeneration,
            generatedForDay: true,
            generatedAt: nowIso,
            lastInputHash: context.inputHash,
            status: 'idle',
            errorMessage: null,
            plannerDiagnostics: response.diagnostics,
          };
        });
      };

      const targetDates = new Set([...slotsByDate.keys(), ...workoutTargetsByDate.keys()]);
      for (const targetDate of targetDates) {
        await applyPlannerTargetsForDate(
          targetDate,
          slotsByDate.get(targetDate) ?? [],
          workoutTargetsByDate.get(targetDate) ?? [],
        );
      }

      if (!targetDates.has(date)) {
        await applyAndCache(date, (body) => {
          body.planGeneration = {
            ...body.planGeneration,
            generatedForDay: true,
            generatedAt: nowIso,
            lastInputHash: context.inputHash,
            status: 'idle',
            errorMessage: null,
            plannerDiagnostics: response.diagnostics,
          };
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to generate meal targets.';
      await applyAndCache(date, (body) => {
        body.planGeneration = {
          ...body.planGeneration,
          status: 'error',
          errorMessage: message,
          plannerDiagnostics: null,
        };
      });
    }
  };

  const queuePlannerGeneration = async (
    date: string,
    source: string,
  ): Promise<void> => {
    if (!(await isMealPlanningEnabled())) {
      return;
    }

    const pending = plannerRunQueueByDate.get(date) ?? Promise.resolve();
    const run = pending
      .catch(() => undefined)
      .then(() => runPlannerGeneration(date, source));
    plannerRunQueueByDate.set(date, run);
    try {
      await run;
    } finally {
      if (plannerRunQueueByDate.get(date) === run) {
        plannerRunQueueByDate.delete(date);
      }
    }
  };

  const invalidatePlannerGenerationForDiaryMutation = (body: DiaryDayBody): void => {
    body.planGeneration = {
      ...(body.planGeneration ?? emptyDiaryDayPlanGeneration()),
      generatedForDay: false,
      generatedAt: null,
      plannerDiagnostics: null,
    };
  };

  /**
   * Re-run the planner for every currently-known day that a newly saved goal
   * governs. A goal is effective from `effectiveFromDate` onward (until the next
   * goal), so we only touch days on/after that date. Scope is intentionally
   * bounded to days we already have in hand — the currently-selected day plus
   * anything in the day cache — rather than unbounded history/future. Days the
   * goal doesn't reach are skipped; days before `effectiveFromDate` are governed
   * by the previous goal and unchanged.
   *
   * No force flag is needed: the resolved goal targets feed the planner input
   * hash (see `buildPlannerRequestContext`), so a changed goal changes the hash,
   * which makes `runPlannerGeneration` treat the day as stale and regenerate it
   * even when it was previously `generatedForDay`.
   */
  const regeneratePlannerForGoalChange = async (
    effectiveFromDate: string,
  ): Promise<void> => {
    if (!(await isMealPlanningEnabled())) {
      return;
    }

    const targetDates = new Set<string>();
    if (selectedDate.value >= effectiveFromDate) {
      targetDates.add(selectedDate.value);
    }
    for (const cachedDate of dayCache.value.keys()) {
      if (cachedDate >= effectiveFromDate) {
        targetDates.add(cachedDate);
      }
    }

    // Deterministic order (dates are ISO YYYY-MM-DD, so lexicographic == chronological).
    for (const date of [...targetDates].sort()) {
      await queuePlannerGeneration(date, 'goalChange');
    }
  };

  // ---------------------------------------------------------------------------
  // Mutations — all route through patchDiaryDay
  // ---------------------------------------------------------------------------

  const applyAndCache = async (
    date: string,
    mutator: (body: DiaryDayBody, day: DiaryDay) => void,
  ): Promise<DiaryDay> => {
    const userId = getUserId();
    const day = await patchDiaryDay(userId, date, (body, dayRef) => {
      // Ensure missing v2 fields are filled before the mutator sees the body.
      const normalized = backfillDiaryDay({ ...dayRef, body }).body;
      // Copy normalized arrays back onto the draft body so mutator-side edits
      // observe the backfilled shape. Entries must be copied too: the legacy
      // exercise-entry migration rewrites their mealPlanId, and dropping that
      // rewrite would re-migrate the same entry into a fresh synthetic workout
      // plan on every mutation.
      body.meals = normalized.meals;
      body.planGeneration = normalized.planGeneration;
      mutator(body, dayRef);
      syncGoalSnapshotForDay(body, date);
    });
    touchCache(day);
    return day;
  };

  const saveDiaryEntry = async (entry: DiaryEntry): Promise<void> => {
    const date = getLoggedDate(entry.loggedAt);
    await applyAndCache(date, (body) => {
      upsertEntryIntoMeals(body, entry);
    });
  };

  const updateDiaryEntry = async (
    entryId: string,
    updates: Partial<DiaryEntry>,
  ): Promise<void> => {
    // Locate the day containing the entry (scan cache first, fall back to any known day)
    const searchDate =
      [...dayCache.value.values()].find((d) =>
        d.body.meals.some((m) => m.entries.some((e) => e.id === entryId)),
      )?.date ?? selectedDate.value;

    await applyAndCache(searchDate, (body) => {
      const current = body.meals
        .flatMap((m) => m.entries)
        .find((e) => e.id === entryId);
      if (!current) return;
      upsertEntryIntoMeals(body, { ...current, ...updates });
    });
  };

  const deleteDiaryEntry = async (entryId: string): Promise<void> => {
    const searchDate =
      [...dayCache.value.values()].find((d) =>
        d.body.meals.some((m) => m.entries.some((e) => e.id === entryId)),
      )?.date ?? selectedDate.value;

    await applyAndCache(searchDate, (body) => {
      const allEntries = () => body.meals.flatMap((m) => m.entries);
      const entry = allEntries().find((candidate) => candidate.id === entryId);
      const matchingFingerprint = entry?.source.type === 'exercise'
        ? workoutEntryFingerprint(entry)
        : null;
      const externalId = entry
        ? intervalsActivityExternalId(entry)
          ?? allEntries()
            .filter((candidate) =>
              candidate.source.type === 'exercise'
              && matchingFingerprint !== null
              && workoutEntryFingerprint(candidate) === matchingFingerprint)
            .map(intervalsActivityExternalId)
            .find((candidate): candidate is string => candidate !== null)
          ?? null
        : null;
      if (!externalId) {
        removeEntryFromMeals(body, entryId);
        return;
      }

      suppressIntervalsActivity(body, externalId, new Date().toISOString());
      for (const meal of body.meals) {
        meal.entries = meal.entries.filter(
          (candidate) =>
            intervalsActivityExternalId(candidate) !== externalId
            && (
              matchingFingerprint === null
              || candidate.source.type !== 'exercise'
              || workoutEntryFingerprint(candidate) !== matchingFingerprint
            ),
        );
      }
    });
  };

  // planOverride was removed from the model; day-level target overrides no
  // longer exist. These retain their signatures as no-ops so lingering callers
  // keep compiling.
  const saveDiaryDayPlan = async (_plan: DiaryDayPlanCompat): Promise<void> => {
    void _plan;
  };

  const updateDiaryDayPlan = async (
    _planId: string,
    _updates: Partial<DiaryDayPlanCompat>,
  ): Promise<void> => {
    void _planId;
    void _updates;
  };

  const deleteDiaryDayPlan = async (_planId: string): Promise<void> => {
    void _planId;
  };

  const saveDiaryMealPlan = async (plan: DiaryMealPlan): Promise<void> => {
    await applyAndCache(plan.date, (body) => {
      const existing = body.meals.find((p) => p.id === plan.id);
      let doc = fromMealPlanCompat(plan, existing);
      const restoredEventExternalId =
        plan.deletedAt === null ? intervalsPlanExternalId(doc) : null;
      if (restoredEventExternalId) {
        doc.externalSource = {
          provider: 'intervals_icu',
          id: restoredEventExternalId,
        };
        restoreIntervalsWorkoutSuppressions(body, restoredEventExternalId);
      }
      // New plans get default periodization metadata (kind, weights).
      if (!existing && !doc.periodization) {
        doc.periodization = defaultPeriodization();
      }
      if (!doc.kind) doc.kind = resolvePlanKind(doc);
      // A newly materialized meal with an explicit target is a user choice: mark
      // its target as a sticky custom override so the planner won't overwrite it.
      if (!existing && doc.kind !== 'workout' && hasMealTarget(doc.planned.target)) {
        doc.custom = { ...(doc.custom ?? {}), target: { ...doc.planned.target! } };
      }

      const idx = body.meals.findIndex((p) => p.id === doc.id);
      if (idx === -1) body.meals.push(doc);
      else body.meals[idx] = doc;
      invalidatePlannerGenerationForDiaryMutation(body);
    });
    await queuePlannerGeneration(plan.date, 'saveDiaryMealPlan');
  };

  const updateDiaryMealPlan = async (
    planId: string,
    updates: Partial<DiaryMealPlan>,
  ): Promise<void> => {
    const date =
      updates.date ??
      [...dayCache.value.values()].find((d) =>
        d.body.meals.some((p) => p.id === planId),
      )?.date ??
      selectedDate.value;

    let shouldRegenerate = false;
    await applyAndCache(date, (body) => {
      const existingPlan = body.meals.find((plan) => plan.id === planId) ?? null;
      const autoRemoveSupportSnackIds =
        existingPlan &&
        resolvePlanKind(existingPlan) !== 'meal' &&
        workoutUpdateAffectsSupportSnacks(updates, toMealPlanCompat(existingPlan, date))
          ? collectAutoRemovableWorkoutSupportSnackIds(body, planId)
          : new Set<string>();

      body.meals = body.meals.map((plan) => {
        if (plan.id !== planId) return plan;
        const compat = toMealPlanCompat(plan, date);
        const next: DiaryMeal = { ...plan, entries: plan.entries };

        // User edits to title/plannedAt/target/exercise become sticky `custom`
        // overrides that win over planner-provided `planned` values.
        const custom: DiaryMealCustom = { ...(plan.custom ?? {}) };
        let customTouched = false;

        if (updates.title !== undefined && updates.title !== compat.title) {
          custom.title = updates.title;
          customTouched = true;
        }
        if (updates.plannedAt !== undefined && updates.plannedAt !== compat.plannedAt) {
          custom.plannedAt = updates.plannedAt;
          customTouched = true;
        }
        const targetEdit: NonNullable<DiaryMealCustom['target']> = { ...(custom.target ?? {}) };
        let targetChanged = false;
        if (updates.targetCalories !== undefined && updates.targetCalories !== compat.targetCalories) { targetEdit.calories = updates.targetCalories; targetChanged = true; }
        if (updates.targetProtein !== undefined && updates.targetProtein !== compat.targetProtein) { targetEdit.protein = updates.targetProtein; targetChanged = true; }
        if (updates.targetCarbs !== undefined && updates.targetCarbs !== compat.targetCarbs) { targetEdit.carbs = updates.targetCarbs; targetChanged = true; }
        if (updates.targetFat !== undefined && updates.targetFat !== compat.targetFat) { targetEdit.fat = updates.targetFat; targetChanged = true; }
        if (targetChanged) { custom.target = targetEdit; customTouched = true; }

        const exEdit: NonNullable<DiaryMealCustom['exercise']> = { ...(custom.exercise ?? {}) };
        let exerciseChanged = false;
        if (updates.exerciseDurationMinutes !== undefined) { exEdit.durationMinutes = updates.exerciseDurationMinutes; exerciseChanged = true; }
        if (updates.exerciseIntensity !== undefined) { exEdit.intensity = updates.exerciseIntensity; exerciseChanged = true; }
        if (updates.exerciseCaloriesBurned !== undefined) { exEdit.caloriesBurned = updates.exerciseCaloriesBurned; exerciseChanged = true; }
        if (updates.exerciseCarbsPerHour !== undefined) { exEdit.carbsPerHour = updates.exerciseCarbsPerHour; exerciseChanged = true; }
        if (updates.exerciseCarbsConsumed !== undefined) { exEdit.carbsConsumed = updates.exerciseCarbsConsumed; exerciseChanged = true; }
        if (updates.exerciseCaloriesConsumed !== undefined) { exEdit.caloriesConsumed = updates.exerciseCaloriesConsumed; exerciseChanged = true; }
        if (exerciseChanged) { custom.exercise = exEdit; customTouched = true; }

        // A view may pass a full `custom` object (e.g. the planning modal).
        if (updates.custom !== undefined) {
          next.custom = updates.custom;
          customTouched = true;
        } else if (customTouched) {
          next.custom = custom;
        }

        if (updates.periodization !== undefined) next.periodization = updates.periodization;
        if (updates.kind !== undefined) next.kind = updates.kind;
        if (updates.completedAt !== undefined) next.completedAt = updates.completedAt;
        if (updates.skippedAt !== undefined) next.skippedAt = updates.skippedAt;
        if (updates.deletedAt !== undefined) next.deletedAt = updates.deletedAt;
        if (updates.workoutActuals !== undefined) next.workoutActuals = updates.workoutActuals;
        if (updates.workoutSupportSnack !== undefined) next.workoutSupportSnack = updates.workoutSupportSnack;
        if (updates.workoutSupportSnackSuppression !== undefined) next.workoutSupportSnackSuppression = updates.workoutSupportSnackSuppression;

        if (
          customTouched ||
          updates.periodization !== undefined ||
          updates.completedAt !== undefined ||
          updates.deletedAt !== undefined ||
          updates.workoutActuals !== undefined
        ) {
          shouldRegenerate = true;
        }
        return next;
      });

      if (autoRemoveSupportSnackIds.size > 0) {
        const removedAt = new Date().toISOString();
        body.meals = body.meals.map((plan) =>
          autoRemoveSupportSnackIds.has(plan.id) ? { ...plan, deletedAt: removedAt } : plan,
        );
        shouldRegenerate = true;
      }

      if (shouldRegenerate) {
        invalidatePlannerGenerationForDiaryMutation(body);
      }
    });
    if (shouldRegenerate) {
      await queuePlannerGeneration(date, 'updateDiaryMealPlan');
    }
  };

  const resetDiaryMealPlanOverrides = async (planId: string): Promise<void> => {
    const date =
      [...dayCache.value.values()].find((d) =>
        d.body.meals.some((p) => p.id === planId),
      )?.date ??
      selectedDate.value;

    await applyAndCache(date, (body) => {
      body.meals = body.meals.map((plan) =>
        plan.id === planId ? clearPlanUserOverrides(plan) : plan,
      );
    });
    await queuePlannerGeneration(date, 'resetDiaryMealPlanOverrides');
  };

  const deleteDiaryMealPlan = async (planId: string): Promise<void> => {
    const date =
      [...dayCache.value.values()].find((d) =>
        d.body.meals.some((p) => p.id === planId),
      )?.date ?? selectedDate.value;

    await applyAndCache(date, (body) => {
      const now = new Date().toISOString();
      const deletedPlan = body.meals.find((p) => p.id === planId) ?? null;
      const supportSnackOwner = deletedPlan ? resolveSupportSnackOwner(body, deletedPlan) : null;
      // Group related plans strictly by shared Intervals.icu external id. We do
      // NOT fall back to a plannedAt/title/metrics fingerprint: matching on time
      // can sweep unrelated plans that merely share a slot into the same
      // deletion. A plan with no external id is deleted on its own.
      const eventExternalId = deletedPlan ? intervalsPlanExternalId(deletedPlan) : null;
      const deletedPlanIds = new Set(
        eventExternalId
          ? body.meals
              .filter((plan) => intervalsPlanExternalId(plan) === eventExternalId)
              .map((plan) => plan.id)
          : [planId],
      );
      // Exercise entries inside the deleted meals that carry an intervals id.
      const linkedActivityExternalIds = new Set(
        body.meals
          .filter((meal) => deletedPlanIds.has(meal.id))
          .flatMap((meal) => meal.entries)
          .filter((entry) => entry.source.type === 'exercise')
          .map(intervalsActivityExternalId)
          .filter((externalId): externalId is string => externalId !== null),
      );
      if (eventExternalId) {
        for (const activityExternalId of linkedActivityExternalIds) {
          suppressIntervalsActivity(
            body,
            activityExternalId,
            now,
            eventExternalId,
          );
        }
      }
      // Move any food entries out of the deleted meals into Unsorted so logged
      // foods are never lost; exercise/completion entries go with the meal.
      const orphanedFood: DiaryDayEntry[] = [];
      for (const meal of body.meals) {
        if (!deletedPlanIds.has(meal.id)) continue;
        for (const entry of meal.entries) {
          if (entry.source.type !== 'exercise') orphanedFood.push(entry);
        }
      }
      body.meals = body.meals.map((p) => {
        if (supportSnackOwner && p.id === supportSnackOwner.workoutPlanId) {
          return withWorkoutSupportSnackSuppressed(p, supportSnackOwner.timing);
        }
        if (!deletedPlanIds.has(p.id)) return p;
        return {
          ...p,
          entries: [],
          externalSource: externalSourceForDeletedPlan(p, now),
          deletedAt: now,
        };
      });
      if (orphanedFood.length > 0) {
        const unsorted = ensureUnsortedMeal(body);
        unsorted.entries.push(...orphanedFood);
      }
      invalidatePlannerGenerationForDiaryMutation(body);
    });
    await queuePlannerGeneration(date, 'deleteDiaryMealPlan');
  };

  const completeDiaryMealPlan = async (planId: string): Promise<void> => {
    const date =
      [...dayCache.value.values()].find((d) =>
        d.body.meals.some((p) => p.id === planId),
      )?.date ?? selectedDate.value;

    await applyAndCache(date, (body) => {
      const planToComplete = body.meals.find((plan) => plan.id === planId) ?? null;
      const consumed = planToComplete ? sumFoodEntriesForPlan(planToComplete) : emptyTotals();
      const isEmpty =
        consumed.calories === 0 &&
        consumed.protein === 0 &&
        consumed.carbs === 0 &&
        consumed.fat === 0;
      const completedAt =
        planToComplete?.completedAt ??
        // A completion time the user established earlier (preserved when the meal
        // was uncompleted) wins over the planned time and "now", so re-completing
        // does not snap the customized time back to the default.
        planToComplete?.custom?.completedAt ??
        // Skipping an empty meal records its scheduled time rather than "now", so
        // the card stays in its planned slot instead of jumping to the moment the
        // user pressed Skip. A meal with food logged still stamps the current time.
        (isEmpty && planToComplete
          ? resolveMealPlannedAt(planToComplete)
          : planToComplete?.custom?.plannedAt ?? new Date().toISOString());

      body.meals = body.meals.map((plan) => {
        if (plan.id !== planId) return plan;
        // Completion locks the target to actuals: write it to `planned` and drop
        // any custom target override so the consumed value is what shows.
        const nextCustom = plan.custom ? { ...plan.custom } : undefined;
        if (nextCustom) delete nextCustom.target;
        return {
          ...plan,
          completedAt,
          skippedAt: isEmpty ? completedAt : null,
          planned: {
            ...plan.planned,
            target: {
              calories: Math.round(consumed.calories),
              protein: Math.round(consumed.protein),
              carbs: Math.round(consumed.carbs),
              fat: Math.round(consumed.fat),
            },
          },
          custom: nextCustom && Object.keys(nextCustom).length > 0 ? nextCustom : undefined,
        };
      });
      invalidatePlannerGenerationForDiaryMutation(body);
    });
    await queuePlannerGeneration(date, 'completeDiaryMealPlan');
  };

  const skipDiaryMealPlan = async (planId: string): Promise<void> => {
    // Skip is just complete-an-empty-meal; the periodization service handles
    // the empty-vs-non-empty branch by setting skippedAt automatically when
    // there are no food entries. To be explicit, force-clear food then complete.
    await completeDiaryMealPlan(planId);
  };

  const uncompleteDiaryMealPlan = async (planId: string): Promise<void> => {
    const date =
      [...dayCache.value.values()].find((d) =>
        d.body.meals.some((p) => p.id === planId),
      )?.date ?? selectedDate.value;

    await applyAndCache(date, (body) => {
      // Preserve the completion time the meal had so a later re-complete keeps it
      // rather than defaulting to "now". uncompletePlanInBody clears completedAt,
      // so read it (or an already-stashed value) before that runs.
      const original = body.meals.find((p) => p.id === planId) ?? null;
      const preservedCompletedAt =
        original?.completedAt ?? original?.custom?.completedAt ?? null;

      // Per spec: uncomplete does NOT trigger automatic recalculation.
      body.meals = uncompletePlanInBody(body, planId).map((p) =>
        p.id === planId
          ? {
              ...p,
              custom: preservedCompletedAt
                ? { ...(p.custom ?? {}), completedAt: preservedCompletedAt }
                : p.custom,
              workoutActuals: null,
              entries: p.entries.filter((e) => e.source.type !== 'exercise'),
            }
          : p,
      );
    });
  };

  const recalculateDiaryDay = async (
    date: string = selectedDate.value,
    source: string = 'manual',
  ): Promise<void> => {
    await queuePlannerGeneration(date, source);
  };

  const completeWorkoutPlan = async (
    planId: string,
    actuals: NonNullable<DiaryMeal['workoutActuals']>,
    // Corrections the athlete made to an already-recorded actual. Stored in
    // `custom.actuals` so a later intervals.icu sync cannot overwrite them.
    actualsOverride?: NonNullable<DiaryMealCustom['actuals']>,
  ): Promise<void> => {
    const date =
      [...dayCache.value.values()].find((d) =>
        d.body.meals.some((p) => p.id === planId),
      )?.date ?? selectedDate.value;

    // Guard: only genuine workout plans may be completed as workouts. A pure
    // meal plan that merely shares a plannedAt slot with a workout must never be
    // stamped with workoutActuals or spawn a phantom exercise burn entry — that
    // double-counts the workout's calories in the daily allowance.
    const guardPlan =
      [...dayCache.value.values()]
        .find((d) => d.date === date)
        ?.body.meals.find((p) => p.id === planId) ?? null;
    if (!guardPlan || resolvePlanKind(guardPlan) === 'meal') return;

    const now = new Date().toISOString();
    await applyAndCache(date, (body) => {
      const plan = body.meals.find((p) => p.id === planId) ?? null;
      // The workout meal owns its exercise entry. Preserve the prior entry's
      // identity (id + externalSource) so an intervals.icu-synced workout stays
      // linked to its activity across re-completion.
      const priorExerciseEntries = (plan?.entries ?? []).filter(
        (e) => e.source.type === 'exercise',
      );
      const priorExerciseEntry =
        priorExerciseEntries.find((e) => e.externalSource) ?? priorExerciseEntries[0] ?? null;

      body.meals = body.meals.map((p) => {
        if (p.id !== planId) return p;
        // Corrections to the recorded actuals go in `custom.actuals`, apart from
        // the planned-phase `custom.exercise` edits a synced activity supersedes.
        const custom: DiaryMealCustom | undefined = actualsOverride
          ? {
              ...(p.custom ?? {}),
              actuals: {
                ...(p.custom?.actuals ?? {}),
                ...actualsOverride,
              },
            }
          : p.custom;
        const nextEntries = p.entries.filter((e) => e.source.type !== 'exercise');
        if (actuals.caloriesBurned > 0) {
          const exerciseEntry: DiaryDayEntry = {
            id: priorExerciseEntry?.id ?? generateId(),
            ...(priorExerciseEntry?.externalSource
              ? { externalSource: priorExerciseEntry.externalSource }
              : {}),
            loggedAt: actuals.completedAt,
            serving: { amount: actuals.durationMinutes, unit: 'minutes' },
            nutritionSnapshot: { calories: actuals.caloriesBurned, protein: 0, carbs: 0, fat: 0 },
            source: { type: 'exercise', label: resolveMealTitle(p) ?? 'Workout' },
            exercise: {
              durationMinutes: actuals.durationMinutes,
              intensity: actuals.intensity ?? 'medium',
            },
            createdAt: priorExerciseEntry?.createdAt ?? now,
          };
          nextEntries.push(exerciseEntry);
        }
        return {
          ...p,
          workoutActuals: actuals,
          completedAt: p.completedAt ?? now,
          custom,
          entries: nextEntries,
        };
      });
      invalidatePlannerGenerationForDiaryMutation(body);
    });
    await queuePlannerGeneration(date, 'completeWorkoutPlan');
  };

  // Save a workout for an exercise that already happened and record the
  // entry's values as its actuals: the entry's time is the workout start, and
  // it ends `duration` later. With `existingEntryId`, that stored entry is first
  // moved into the workout so `completeWorkoutPlan` rebuilds it in place,
  // keeping its id and any intervals.icu link, instead of adding a second burn.
  const saveCompletedWorkoutFromEntry = async (
    entry: DiaryEntry,
    date: string,
    existingEntryId: string | null,
  ): Promise<string> => {
    const durationMinutes = entry.exercise?.durationMinutes ?? entry.serving.amount;
    const now = new Date().toISOString();
    const id = generateId();
    await saveDiaryMealPlan({
      id,
      date,
      plannedAt: entry.loggedAt,
      mealId: null,
      kind: 'workout',
      title: entry.source.label?.trim() || 'Workout',
      targetCalories: 0,
      targetProtein: 0,
      targetCarbs: 0,
      targetFat: 0,
      createdAt: now,
      updatedAt: now,
    });
    if (existingEntryId) {
      await updateDiaryEntry(existingEntryId, { mealPlanId: id });
    }
    await completeWorkoutPlan(id, {
      durationMinutes,
      intensity: entry.exercise?.intensity ?? null,
      caloriesBurned: entry.nutritionSnapshot.calories ?? 0,
      fuelCarbsPerHour: null,
      fuelCalories: 0,
      fuelCarbs: 0,
      completedAt: addMinutesIso(entry.loggedAt, durationMinutes),
    });
    return id;
  };

  /**
   * Log an exercise that already happened (the nav bar's and Diary page's "Add
   * exercise"). Its values are recorded as *actuals* on a completed workout, not
   * as a plan. This keeps it counted as done — including in the planner's
   * rolling balance, which only counts past workouts that have an actual burn.
   *
   * Returns the new workout's id. `completeWorkoutPlan` builds the workout's
   * exercise entry from the actuals, so `entry` itself is not stored.
   */
  const logCompletedWorkout = (entry: DiaryEntry): Promise<string> =>
    saveCompletedWorkoutFromEntry(entry, getLoggedDate(entry.loggedAt), null);

  /**
   * Promote an already-stored exercise entry that has no workout (a legacy
   * manual log, or an intervals.icu activity from before the backend created
   * workout meals for unpaired activities) to a completed workout with the
   * entry's values as actuals. The entry is a record of something that
   * happened, so it must not become a planned-only workout that drops out of
   * past days' rolling balance.
   */
  const promoteExerciseEntryToCompletedWorkout = (
    entry: DiaryEntry,
    date: string,
  ): Promise<string> => saveCompletedWorkoutFromEntry(entry, date, entry.id);

  const updateEntriesForFoodRevision = async (
    previousFood: FoodItem,
    nextFood: FoodItem,
  ): Promise<void> => {
    const userId = getUserId();
    const days = await getAllDiaryDays();

    for (const day of days) {
      if (day.userId !== userId) continue;
      const affectsDay = day.body.meals.some((meal) =>
        meal.entries.some((entry) => entry.source.foodId === previousFood.id),
      );
      if (!affectsDay) continue;

      const remapEntry = (entry: DiaryDayEntry): DiaryDayEntry => {
        if (entry.source.foodId !== previousFood.id) return entry;
        const matchingServing = nextFood.servingSizes.find(
          (serving) => serving.label === entry.serving.unit,
        );
        if (!matchingServing) return entry;
        return {
          ...entry,
          nutritionSnapshot: scaleFoodServingNutrition(matchingServing, entry.serving.amount),
          source: {
            ...entry.source,
            type: 'custom',
            label: nextFood.name,
            foodId: nextFood.id,
          },
        };
      };

      const updatedDay = await patchDiaryDay(userId, day.date, (body) => {
        body.meals = body.meals.map((meal) => ({
          ...meal,
          entries: meal.entries.map(remapEntry),
        }));
      });

      touchCache(updatedDay);
    }
  };

  const deleteDailyNutritionTotal = async (entryId: string): Promise<void> => {
    const date = entryId.startsWith('imported:')
      ? entryId.slice('imported:'.length)
      : selectedDate.value;
    await applyAndCache(date, (body) => {
      body.importedTotals = null;
    });
  };

  // ---------------------------------------------------------------------------
  // Legacy helpers still expected by callers
  // ---------------------------------------------------------------------------

  const getAllEntries = async (): Promise<DiaryEntry[]> => {
    // Searches across all loaded days (used by SearchStage for "recent foods")
    const days = await getAllDiaryDays();
    return days.flatMap((d) => d.body.meals.flatMap((m) => m.entries));
  };

  const getDaysInRange = async (from: string, to: string): Promise<DiaryDay[]> => {
    const userId = getUserId();
    return getDiaryDaysInRange(userId, from, to);
  };

  // Preserved no-op for backwards compatibility — dailyNutritionTotals are now
  // embedded in DiaryDay. Views that called this during startup still work.
  const loadDiaryEntries = async (): Promise<void> => {
    await loadCurrentDay();
  };
  const loadDailyNutritionTotals = async (): Promise<void> => {
    // no-op — projected from body.importedTotals
  };
  const loadDiaryDayPlans = async (): Promise<void> => {
    // no-op — projected from body.planOverride
  };
  const loadDiaryMealPlans = async (): Promise<void> => {
    // no-op — projected from body.meals
  };

  const setPlanningAddFoodTarget = (target: DiaryPlanningAddFoodTarget | null): void => {
    planningAddFoodTarget.value = target;
  };

  return {
    // State
    selectedDate,
    goalSets,
    foodItems,
    planningAddFoodTarget,
    isLoading,
    hasLoadedData,

    // Derived
    currentDay,
    currentBody,
    diaryEntries,
    activeGoalSet,
    entriesForSelectedDate,
    foodEntriesForSelectedDate,
    exerciseEntriesForSelectedDate,
    exerciseCaloriesBurned,
    dailyTotals,
    importedDailyTotal,
    hasImportedDailyTotal,
    effectiveDailyTotals,
    mealTotalsFor,
    mealPlanTotalsFor,
    dayPlanForSelectedDate,
    mealPlansForSelectedDate,
    mealPlanGenerationState,
    entriesForMealPlan,
    mealContextForEntry,
    dailyGoals,
    remainingSummary,

    // Actions
    loadAllData,
    ensureDataLoaded,
    invalidateAfterSync,
    loadGoalSets,
    loadFoodItems,
    loadCurrentDay,
    loadDiaryEntries,
    loadDailyNutritionTotals,
    loadDiaryDayPlans,
    loadDiaryMealPlans,
    setSelectedDate,
    saveDiaryEntry,
    deleteDiaryEntry,
    updateDiaryEntry,
    deleteDailyNutritionTotal,
    saveDiaryDayPlan,
    deleteDiaryDayPlan,
    updateDiaryDayPlan,
    setPlanningAddFoodTarget,
    saveDiaryMealPlan,
    deleteDiaryMealPlan,
    updateDiaryMealPlan,
    resetDiaryMealPlanOverrides,
    completeDiaryMealPlan,
    skipDiaryMealPlan,
    uncompleteDiaryMealPlan,
    recalculateDiaryDay,
    completeWorkoutPlan,
    logCompletedWorkout,
    promoteExerciseEntryToCompletedWorkout,
    regeneratePlannerForGoalChange,
    updateEntriesForFoodRevision,
    getAllEntries,
    getDaysInRange,
  };
});
